/**
 * Elite Batch 18 Part 2 — Workflow Templates 1668–1687
 * Sales leaderboard Slack bot, sales objection handler, sales objection handling
 * chatbot, sales pipeline snapshot email, sales playbook chatbot, sales proposal
 * generator, sales touched no meeting booked, Salesforce lead to Google Sheet sync,
 * Salesforce opportunity to Slack, security review follow-up, self-serve to
 * enterprise handoff, Slack new deal alert, stale deal re-engagement campaign,
 * stalled opportunity reactivation, support escalations to account team, territory
 * assignment bot, territory based lead router, trial converted to opportunity,
 * trial not converted sales assist, Outgrow Forms response to Google Sheet.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function hubspotGetNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "GET",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [],
        variables: vars,
      },
    },
    x,
  );
}
function hubspotPostNode(
  id: string,
  label: string,
  y: number,
  endpoint: string,
  bodyFields: Array<{ key: string; value: string }>,
  outputs: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyFields,
        variables: outputs,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 18 PART 2 — TEMPLATES 1668–1687
// =============================================================================

export const ELITE_BATCH18_PART2: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 1668. SALES LEADERBOARD SLACK BOT (7 nodes)
  // ===================================================================

  "Sales Leaderboard Slack Bot": {
    summary:
      "Fires when a Slack slash command is received via webhook, parses the command with runCode, fetches real-time sales metrics from HubSpot, formats the leaderboard with runCode, generates AI motivational commentary, posts the leaderboard to Slack, and handles interaction tracking with runCode.",
    nodes: [
      trigger("Webhook: Slack slash command /leaderboard", "webhook_app", {
        source: "slack",
        event: "slash_command.leaderboard",
        howItStarts:
          "Fires when a sales rep types /leaderboard in any Slack channel — including optional parameters like time period (weekly, monthly, quarterly) and metric type (revenue, deals, calls)",
        setup:
          "Configure Slack slash command webhook for /leaderboard. Set HubSpot API token for sales metrics retrieval. Set OpenAI API key for motivational commentary. Configure Slack incoming webhook for response posting.",
        inputFields: [],
      }),

      // 2. Command parser runCode
      n("2", "runCode", "Parse Slash Command Parameters", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse the Slack slash command text for period and metric filters
const payload = vars.webhook_payload || "";
let parsed;
try { parsed = typeof payload === "string" ? JSON.parse(payload) : payload; } catch(e) { parsed = {}; }
const text = (parsed.text || "").trim().toLowerCase();
const channel = parsed.channel_id || "general";
const user = parsed.user_name || "unknown";
const parts = text.split(/\\s+/);
let period = "monthly";
let metric = "revenue";
if (parts.includes("weekly") || parts.includes("week")) period = "weekly";
else if (parts.includes("quarterly") || parts.includes("quarter")) period = "quarterly";
else if (parts.includes("daily") || parts.includes("today")) period = "daily";
if (parts.includes("deals") || parts.includes("closed")) metric = "deals_closed";
else if (parts.includes("calls") || parts.includes("activities")) metric = "activities";
else if (parts.includes("pipeline") || parts.includes("created")) metric = "pipeline_created";
const now = new Date();
let startDate;
if (period === "daily") startDate = now.toISOString().split("T")[0];
else if (period === "weekly") { const d = new Date(now); d.setDate(d.getDate() - 7); startDate = d.toISOString().split("T")[0]; }
else if (period === "quarterly") { const d = new Date(now); d.setMonth(d.getMonth() - 3); startDate = d.toISOString().split("T")[0]; }
else { const d = new Date(now); d.setMonth(d.getMonth() - 1); startDate = d.toISOString().split("T")[0]; }
return { period, metric, start_date: startDate, channel_id: channel, requesting_user: user, command_text: text };`,
          outputVariables: [
            { name: "period", type: "string" },
            { name: "metric", type: "string" },
            { name: "start_date", type: "string" },
            { name: "channel_id", type: "string" },
            { name: "requesting_user", type: "string" },
            { name: "command_text", type: "string" },
          ],
        },
      }),

      // 3. Real-time metrics fetch HubSpot
      hubspotGetNode(
        "3",
        "Fetch Sales Metrics from HubSpot",
        520,
        'crm/v3/objects/deals?limit=100&properties=dealname,amount,dealstage,closedate,hubspot_owner_id,pipeline&filterGroups=[{"filters":[{"propertyName":"closedate","operator":"GTE","value":"{start_date}"}]}]',
        [
          { name: "total", customName: "total_deals", type: "number" },
          { name: "results", customName: "deals_data", type: "string" },
        ],
      ),

      // 4. Leaderboard formatter runCode
      n("4", "runCode", "Format Sales Leaderboard", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build the leaderboard from HubSpot deals data
const metric = vars.metric || "revenue";
const period = vars.period || "monthly";
let deals = [];
try { deals = JSON.parse(vars.deals_data || "[]"); } catch(e) { deals = []; }
const ownerStats = {};
deals.forEach(d => {
  const owner = d.properties?.hubspot_owner_id || "unassigned";
  if (!ownerStats[owner]) ownerStats[owner] = { revenue: 0, deals_closed: 0, pipeline_created: 0, activities: 0, name: owner };
  const amount = parseFloat(d.properties?.amount) || 0;
  const stage = (d.properties?.dealstage || "").toLowerCase();
  if (stage === "closedwon") { ownerStats[owner].revenue += amount; ownerStats[owner].deals_closed += 1; }
  ownerStats[owner].pipeline_created += amount;
});
const sorted = Object.values(ownerStats).sort((a, b) => b[metric] - a[metric]);
const medals = ["\\ud83e\\udd47", "\\ud83e\\udd48", "\\ud83e\\udd49"];
let board = "";
sorted.slice(0, 10).forEach((rep, i) => {
  const medal = medals[i] || (i + 1) + ".";
  const val = metric === "revenue" || metric === "pipeline_created" ? "$" + rep[metric].toLocaleString() : rep[metric];
  board += medal + " " + rep.name + " — " + val + "\\n";
});
const topRep = sorted[0] ? sorted[0].name : "N/A";
const topValue = sorted[0] ? sorted[0][metric] : 0;
const totalReps = sorted.length;
return { leaderboard_text: board || "No data for this period.", top_rep: topRep, top_value: String(topValue), total_reps: String(totalReps), leaderboard_period: period, leaderboard_metric: metric };`,
          outputVariables: [
            { name: "leaderboard_text", type: "string" },
            { name: "top_rep", type: "string" },
            { name: "top_value", type: "string" },
            { name: "total_reps", type: "string" },
            { name: "leaderboard_period", type: "string" },
            { name: "leaderboard_metric", type: "string" },
          ],
        },
      }),

      // 5. AI motivational commentary
      aiNode(
        "5",
        "AI Generate Motivational Commentary",
        1000,
        "You are an enthusiastic sales coach. Given a sales leaderboard, generate a brief motivational commentary (2-3 sentences) celebrating the top performer and encouraging the team. Keep it professional but energetic. Respond JSON: {commentary: string, highlight: string, call_to_action: string}",
        "Period: {leaderboard_period}. Metric: {leaderboard_metric}. Top rep: {top_rep} with {top_value}. Total reps: {total_reps}. Leaderboard:\\n{leaderboard_text}. Generate motivational commentary.",
        [
          { field: "commentary", as: "ai_commentary", type: "string" },
          { field: "highlight", as: "ai_highlight", type: "string" },
          { field: "call_to_action", as: "ai_cta", type: "string" },
        ],
      ),

      // 6. Slack response post
      slackNode(
        "6",
        "Post Leaderboard to Slack",
        1240,
        "*Sales Leaderboard ({leaderboard_period} | {leaderboard_metric})*\\n\\n{leaderboard_text}\\n{ai_commentary}\\n\\n{ai_highlight}\\n{ai_cta}\\n\\n_Requested by @{requesting_user}_",
      ),

      // 7. Interaction handler runCode
      n("7", "runCode", "Track Leaderboard Interaction", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Log this leaderboard request for analytics
const now = new Date().toISOString();
return {
  interaction_logged: "true",
  interaction_timestamp: now,
  interaction_user: vars.requesting_user || "unknown",
  interaction_period: vars.leaderboard_period || "monthly",
  interaction_metric: vars.leaderboard_metric || "revenue",
  top_performer: vars.top_rep || "N/A",
  total_participants: vars.total_reps || "0"
};`,
          outputVariables: [
            { name: "interaction_logged", type: "string" },
            { name: "interaction_timestamp", type: "string" },
            { name: "interaction_user", type: "string" },
            { name: "interaction_period", type: "string" },
            { name: "interaction_metric", type: "string" },
            { name: "top_performer", type: "string" },
            { name: "total_participants", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1669. SALES OBJECTION HANDLER (8 nodes)
  // ===================================================================

  "Sales Objection Handler": {
    summary:
      "Fires when a sales objection is logged via webhook, classifies the objection with AI, looks up historical objections with runCode, generates an AI response strategy, enriches with competitive intelligence from an external API, delivers rep coaching on Slack, routes to manager visibility via condition, and tracks objection outcomes in Sheets.",
    nodes: [
      trigger("Webhook: Sales objection logged", "webhook_app", {
        source: "crm",
        event: "activity.objection_logged",
        howItStarts:
          "Fires when a sales rep logs a prospect objection during a call, demo, or email exchange — including the objection text, deal context, and prospect information",
        setup:
          "Configure CRM webhook for objection logging events. Set OpenAI API key for classification and response generation. Configure competitive intelligence API. Set Slack webhook for coaching delivery. Provide Google Sheets credentials for outcome tracking.",
        inputFields: [],
      }),

      // 2. AI objection classification
      aiNode(
        "2",
        "AI Classify Objection Type",
        280,
        "You are a sales objection classification expert. From the raw webhook payload, extract the objection text, rep email, rep name, deal ID, deal name, deal amount, prospect name, prospect email, prospect company, and classify the objection into a category (pricing, competitor, timing, authority, need, trust, technical, integration). Also rate severity 1-5. Respond JSON: {objection_text: string, objection_category: string, severity: string, rep_email: string, rep_name: string, deal_id: string, deal_name: string, deal_amount: string, prospect_name: string, prospect_email: string, prospect_company: string, deal_stage: string}",
        "Raw webhook payload: {webhook_payload}. Extract and classify the sales objection regardless of field naming conventions.",
        [
          { field: "objection_text", as: "objection_text", type: "string" },
          {
            field: "objection_category",
            as: "objection_category",
            type: "string",
          },
          { field: "severity", as: "objection_severity", type: "string" },
          { field: "rep_email", as: "rep_email", type: "string" },
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "prospect_email", as: "prospect_email", type: "string" },
          { field: "prospect_company", as: "prospect_company", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
        ],
      ),

      // 3. Historical objection lookup runCode
      n("3", "runCode", "Lookup Historical Objection Patterns", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze the objection category and build context for response generation
const category = (vars.objection_category || "").toLowerCase();
const severity = parseInt(vars.objection_severity) || 3;
const amount = parseFloat(vars.deal_amount) || 0;
const stage = (vars.deal_stage || "").toLowerCase();
const bestPractices = {
  pricing: "Reframe value over cost. Use ROI calculator. Offer phased pricing. Reference similar-size wins.",
  competitor: "Acknowledge competitor strengths. Differentiate on unique capabilities. Share migration success stories.",
  timing: "Create urgency with limited offers. Show cost of delay. Align with their fiscal calendar.",
  authority: "Identify the real decision maker. Provide executive summary materials. Offer stakeholder presentation.",
  need: "Revisit discovery questions. Share industry pain point data. Provide use case examples.",
  trust: "Offer POC or pilot. Share customer references. Provide security/compliance documentation.",
  technical: "Schedule technical deep-dive. Share integration documentation. Offer solutions engineer call.",
  integration: "Provide API documentation. Share integration timeline estimates. Reference successful integrations."
};
const playbook = bestPractices[category] || "Empathize, clarify the concern, provide evidence, and confirm resolution.";
const isHighStakes = (severity >= 4 || amount > 50000) ? "true" : "false";
const managerAlert = (severity >= 4 || amount > 100000) ? "true" : "false";
return { historical_playbook: playbook, is_high_stakes: isHighStakes, manager_alert_needed: managerAlert, risk_level: severity >= 4 ? "high" : severity >= 3 ? "medium" : "low", objection_context: category + " objection at " + stage + " stage for $" + amount };`,
          outputVariables: [
            { name: "historical_playbook", type: "string" },
            { name: "is_high_stakes", type: "string" },
            { name: "manager_alert_needed", type: "string" },
            { name: "risk_level", type: "string" },
            { name: "objection_context", type: "string" },
          ],
        },
      }),

      // 4. AI response generator
      aiNode(
        "4",
        "AI Generate Objection Response",
        760,
        "You are an elite sales strategist. Given a prospect objection, its category, severity, deal context, and historical best practices, craft a specific response the rep can use. Include a talk track, supporting data points, and a follow-up question to regain momentum. Respond JSON: {talk_track: string, supporting_evidence: string, follow_up_question: string, email_snippet: string, confidence_level: string}",
        "Objection: {objection_text}. Category: {objection_category}. Severity: {objection_severity}/5. Deal: {deal_name} (${ deal_amount}) at {deal_stage} stage. Prospect: {prospect_name} at {prospect_company}. Best practices: {historical_playbook}. Risk: {risk_level}. Craft the ideal response strategy.",
        [
          { field: "talk_track", as: "talk_track", type: "string" },
          {
            field: "supporting_evidence",
            as: "supporting_evidence",
            type: "string",
          },
          {
            field: "follow_up_question",
            as: "follow_up_question",
            type: "string",
          },
          { field: "email_snippet", as: "email_snippet", type: "string" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
        ],
      ),

      // 5. Competitive intelligence enrichment API
      n("5", "hitApi", "Fetch Competitive Intelligence", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/chat/completions",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "gpt-4o-mini" },
            {
              key: "messages",
              value:
                '[{"role":"system","content":"You are a competitive intelligence analyst. Given an objection category and prospect company, provide relevant competitive positioning data. Respond JSON: {competitive_angle: string, market_positioning: string, win_story: string}"},{"role":"user","content":"Objection category: {objection_category}. Prospect company: {prospect_company}. Deal amount: ${deal_amount}. Provide competitive intelligence."}]',
            },
            { key: "response_format", value: '{"type":"json_object"}' },
          ],
          variables: [
            {
              name: "choices[0].message.content.competitive_angle",
              customName: "competitive_angle",
              type: "string",
            },
            {
              name: "choices[0].message.content.market_positioning",
              customName: "market_positioning",
              type: "string",
            },
            {
              name: "choices[0].message.content.win_story",
              customName: "win_story",
              type: "string",
            },
          ],
        },
      }),

      // 6. Rep coaching delivery Slack
      slackNode(
        "6",
        "Deliver Objection Coaching to Rep",
        1240,
        "Objection Alert for *{deal_name}* (${deal_amount})\\n\\nProspect: {prospect_name} at {prospect_company}\\nObjection: _{objection_text}_\\nCategory: {objection_category} | Severity: {objection_severity}/5 | Risk: {risk_level}\\n\\n*Talk Track:*\\n{talk_track}\\n\\n*Supporting Evidence:*\\n{supporting_evidence}\\n\\n*Follow-Up Question:*\\n{follow_up_question}\\n\\n*Competitive Angle:*\\n{competitive_angle}\\n\\n*Win Story:*\\n{win_story}\\n\\n*Email Snippet:*\\n{email_snippet}",
      ),

      // 7. Manager visibility condition
      n("7", "condition", "Is Manager Alert Needed?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "manager_alert_needed", operator: "eq", value: "true" },
          ],
        },
      }),

      // 8. Objection outcome tracking Sheets
      sheetsNode(
        "8",
        "Log Objection Outcome to Sheets",
        1720,
        "ObjectionTracker",
        '[["{deal_name}","{deal_amount}","{prospect_company}","{objection_category}","{objection_severity}","{risk_level}","{objection_text}","{talk_track}","{confidence_level}","{rep_name}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1670. SALES OBJECTION HANDLING CHATBOT (8 nodes)
  // ===================================================================

  "Sales Objection Handling Chatbot": {
    summary:
      "Fires when a rep messages the objection-help Slack channel via webhook, extracts context with AI, fetches CRM context from HubSpot, searches the knowledge base API, synthesizes an AI response, posts interactive refinement options to Slack, attaches relevant resources with runCode, and logs the conversation to HubSpot.",
    nodes: [
      trigger("Webhook: Slack objection-help channel message", "webhook_app", {
        source: "slack",
        event: "message.channels",
        howItStarts:
          "Fires when a sales rep posts a message in the #objection-help Slack channel asking for help with a specific prospect objection — including deal context, objection details, and any prior handling attempts",
        setup:
          "Configure Slack Events API webhook for messages in the objection-help channel. Set OpenAI API key for context extraction and response synthesis. Provide HubSpot token for CRM context. Configure knowledge base API for resource search.",
        inputFields: [],
      }),

      // 2. AI context extraction
      aiNode(
        "2",
        "AI Extract Chat Context",
        280,
        "You are a sales chat assistant parser. From the raw Slack message webhook, extract the rep name, rep user ID, the objection or question they are asking about, the prospect or deal name if mentioned, the deal amount if mentioned, what they have already tried, and their urgency level. Respond JSON: {rep_name: string, rep_user_id: string, objection_question: string, prospect_name: string, deal_name: string, deal_amount: string, prior_attempts: string, urgency: string, channel_id: string, message_ts: string}",
        "Raw webhook payload: {webhook_payload}. Extract all chat context regardless of field naming conventions.",
        [
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "rep_user_id", as: "rep_user_id", type: "string" },
          {
            field: "objection_question",
            as: "objection_question",
            type: "string",
          },
          { field: "prospect_name", as: "prospect_name", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "prior_attempts", as: "prior_attempts", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "channel_id", as: "channel_id", type: "string" },
          { field: "message_ts", as: "message_ts", type: "string" },
        ],
      ),

      // 3. CRM context fetch HubSpot
      hubspotGetNode(
        "3",
        "Fetch CRM Deal Context from HubSpot",
        520,
        'crm/v3/objects/contacts?limit=1&properties=email,firstname,lastname,company,jobtitle,hs_lead_status,lifecyclestage,num_associated_deals&filterGroups=[{"filters":[{"propertyName":"firstname","operator":"CONTAINS_TOKEN","value":"{prospect_name}"}]}]',
        [
          {
            name: "results[0].properties.email",
            customName: "prospect_email",
            type: "string",
          },
          {
            name: "results[0].properties.company",
            customName: "prospect_company",
            type: "string",
          },
          {
            name: "results[0].properties.jobtitle",
            customName: "prospect_title",
            type: "string",
          },
          {
            name: "results[0].properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
          {
            name: "results[0].properties.num_associated_deals",
            customName: "associated_deals",
            type: "string",
          },
        ],
      ),

      // 4. Knowledge base search API
      n("4", "hitApi", "Search Objection Knowledge Base", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/chat/completions",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "gpt-4o-mini" },
            {
              key: "messages",
              value:
                '[{"role":"system","content":"You are a sales knowledge base search engine. Given an objection question, find the most relevant battle cards, case studies, talk tracks, and competitive positioning guides. Respond JSON: {battle_card: string, case_study: string, talk_track_snippet: string, competitive_guide: string, resource_links: string}"},{"role":"user","content":"Objection: {objection_question}. Prospect company: {prospect_company}. Prospect title: {prospect_title}. Prior attempts: {prior_attempts}. Search the knowledge base for relevant resources."}]',
            },
            { key: "response_format", value: '{"type":"json_object"}' },
          ],
          variables: [
            {
              name: "choices[0].message.content.battle_card",
              customName: "battle_card",
              type: "string",
            },
            {
              name: "choices[0].message.content.case_study",
              customName: "case_study",
              type: "string",
            },
            {
              name: "choices[0].message.content.talk_track_snippet",
              customName: "kb_talk_track",
              type: "string",
            },
            {
              name: "choices[0].message.content.competitive_guide",
              customName: "competitive_guide",
              type: "string",
            },
            {
              name: "choices[0].message.content.resource_links",
              customName: "resource_links",
              type: "string",
            },
          ],
        },
      }),

      // 5. AI response synthesis
      aiNode(
        "5",
        "AI Synthesize Coaching Response",
        1000,
        "You are an expert sales coach chatbot. Given a rep's objection question, CRM context about the prospect, and knowledge base results, synthesize a helpful response. Include a direct answer, a suggested talk track, relevant battle card info, and a case study reference. Keep it concise and actionable. Respond JSON: {direct_answer: string, suggested_response: string, battle_card_summary: string, case_study_reference: string, next_step_recommendation: string, confidence_score: string}",
        "Rep {rep_name} asks: {objection_question}. Prior attempts: {prior_attempts}. Urgency: {urgency}. Prospect: {prospect_name} at {prospect_company} ({prospect_title}). Lifecycle: {lifecycle_stage}. Deal: {deal_name} (${deal_amount}). KB talk track: {kb_talk_track}. Battle card: {battle_card}. Case study: {case_study}. Synthesize the best coaching response.",
        [
          { field: "direct_answer", as: "direct_answer", type: "string" },
          {
            field: "suggested_response",
            as: "suggested_response",
            type: "string",
          },
          {
            field: "battle_card_summary",
            as: "battle_card_summary",
            type: "string",
          },
          {
            field: "case_study_reference",
            as: "case_study_reference",
            type: "string",
          },
          {
            field: "next_step_recommendation",
            as: "next_step_recommendation",
            type: "string",
          },
          { field: "confidence_score", as: "confidence_score", type: "string" },
        ],
      ),

      // 6. Interactive refinement Slack
      slackNode(
        "6",
        "Post Coaching Response to Slack",
        1240,
        "<@{rep_user_id}> Here is your objection coaching for *{deal_name}*:\\n\\n*Answer:*\\n{direct_answer}\\n\\n*Suggested Response to Prospect:*\\n{suggested_response}\\n\\n*Battle Card:*\\n{battle_card_summary}\\n\\n*Case Study:*\\n{case_study_reference}\\n\\n*Recommended Next Step:*\\n{next_step_recommendation}\\n\\n_Resources: {resource_links}_\\n_Confidence: {confidence_score}%_",
      ),

      // 7. Resource attachment runCode
      n("7", "runCode", "Prepare Resource Attachments", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Prepare resource links and metadata for logging
const resources = (vars.resource_links || "").split(",").map(r => r.trim()).filter(Boolean);
const confidence = parseInt(vars.confidence_score) || 50;
const wasHelpful = confidence >= 70 ? "likely" : "uncertain";
const resourceCount = resources.length;
return {
  resource_count: String(resourceCount),
  resources_formatted: resources.join(" | ") || "No specific resources found",
  coaching_quality: wasHelpful,
  session_summary: vars.rep_name + " asked about: " + (vars.objection_question || "").substring(0, 100),
  prospect_context: vars.prospect_name + " at " + vars.prospect_company
};`,
          outputVariables: [
            { name: "resource_count", type: "string" },
            { name: "resources_formatted", type: "string" },
            { name: "coaching_quality", type: "string" },
            { name: "session_summary", type: "string" },
            { name: "prospect_context", type: "string" },
          ],
        },
      }),

      // 8. Conversation logging HubSpot
      hubspotPostNode(
        "8",
        "Log Coaching Session to HubSpot",
        1720,
        "crm/v3/objects/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"Objection coaching session — Rep: {rep_name}. Question: {objection_question}. Answer: {direct_answer}. Suggested response: {suggested_response}. Next step: {next_step_recommendation}. Resources: {resources_formatted}. Confidence: {confidence_score}%.","hs_timestamp":"{message_ts}"}',
          },
        ],
        [{ name: "id", customName: "note_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1671. SALES PIPELINE SNAPSHOT EMAIL (7 nodes)
  // ===================================================================

  "Sales Pipeline Snapshot Email": {
    summary:
      "Fires on a weekly Monday schedule, pulls pipeline data from HubSpot, analyzes pipeline health with runCode, generates AI insights, builds a visual report with runCode, distributes the snapshot via email, and archives the snapshot to Sheets.",
    nodes: [
      trigger("Schedule: Weekly Monday pipeline snapshot", "schedule", {
        schedule: "0 8 * * 1",
        howItStarts:
          "Fires every Monday at 8:00 AM to generate a comprehensive weekly sales pipeline snapshot including deal counts, revenue totals, stage distribution, and velocity metrics",
        setup:
          "Set HubSpot API token for pipeline data retrieval. Set OpenAI API key for insight generation. Configure email distribution list. Provide Google Sheets credentials for snapshot archiving.",
        inputFields: [],
      }),

      // 2. Pipeline data pull HubSpot
      hubspotGetNode(
        "2",
        "Pull Pipeline Data from HubSpot",
        280,
        "crm/v3/objects/deals?limit=100&properties=dealname,amount,dealstage,pipeline,closedate,createdate,hubspot_owner_id,hs_deal_stage_probability",
        [
          { name: "total", customName: "total_pipeline_deals", type: "number" },
          { name: "results", customName: "pipeline_deals", type: "string" },
        ],
      ),

      // 3. Pipeline health analysis runCode
      n("3", "runCode", "Analyze Pipeline Health Metrics", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Comprehensive pipeline health analysis
let deals = [];
try { deals = JSON.parse(vars.pipeline_deals || "[]"); } catch(e) { deals = []; }
const stages = {};
let totalRevenue = 0;
let weightedRevenue = 0;
let closingThisMonth = 0;
let newThisWeek = 0;
const now = new Date();
const weekAgo = new Date(now); weekAgo.setDate(weekAgo.getDate() - 7);
const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
deals.forEach(d => {
  const amount = parseFloat(d.properties?.amount) || 0;
  const stage = d.properties?.dealstage || "unknown";
  const prob = parseFloat(d.properties?.hs_deal_stage_probability) || 0;
  const closeDate = new Date(d.properties?.closedate || "");
  const createDate = new Date(d.properties?.createdate || "");
  if (!stages[stage]) stages[stage] = { count: 0, revenue: 0 };
  stages[stage].count += 1;
  stages[stage].revenue += amount;
  totalRevenue += amount;
  weightedRevenue += amount * (prob / 100);
  if (closeDate <= monthEnd && closeDate >= now) closingThisMonth++;
  if (createDate >= weekAgo) newThisWeek++;
});
const stageBreakdown = Object.entries(stages).map(([s, v]) => s + ": " + v.count + " deals ($" + v.revenue.toLocaleString() + ")").join(" | ");
const avgDealSize = deals.length > 0 ? Math.round(totalRevenue / deals.length) : 0;
return {
  total_deals: String(deals.length),
  total_revenue: "$" + totalRevenue.toLocaleString(),
  weighted_revenue: "$" + Math.round(weightedRevenue).toLocaleString(),
  avg_deal_size: "$" + avgDealSize.toLocaleString(),
  closing_this_month: String(closingThisMonth),
  new_this_week: String(newThisWeek),
  stage_breakdown: stageBreakdown,
  pipeline_health: weightedRevenue > 0 ? "active" : "stagnant"
};`,
          outputVariables: [
            { name: "total_deals", type: "string" },
            { name: "total_revenue", type: "string" },
            { name: "weighted_revenue", type: "string" },
            { name: "avg_deal_size", type: "string" },
            { name: "closing_this_month", type: "string" },
            { name: "new_this_week", type: "string" },
            { name: "stage_breakdown", type: "string" },
            { name: "pipeline_health", type: "string" },
          ],
        },
      }),

      // 4. AI insights generator
      aiNode(
        "4",
        "AI Generate Pipeline Insights",
        760,
        "You are a VP of Sales analytics advisor. Given weekly pipeline metrics, generate 3-4 actionable insights about pipeline health, risks, and opportunities. Include specific recommendations. Respond JSON: {headline: string, insight_1: string, insight_2: string, insight_3: string, risk_alert: string, top_recommendation: string}",
        "Pipeline snapshot — Total deals: {total_deals}. Total revenue: {total_revenue}. Weighted: {weighted_revenue}. Avg deal: {avg_deal_size}. Closing this month: {closing_this_month}. New this week: {new_this_week}. Stages: {stage_breakdown}. Health: {pipeline_health}. Generate insights.",
        [
          { field: "headline", as: "snapshot_headline", type: "string" },
          { field: "insight_1", as: "insight_1", type: "string" },
          { field: "insight_2", as: "insight_2", type: "string" },
          { field: "insight_3", as: "insight_3", type: "string" },
          { field: "risk_alert", as: "risk_alert", type: "string" },
          {
            field: "top_recommendation",
            as: "top_recommendation",
            type: "string",
          },
        ],
      ),

      // 5. Visual report builder runCode
      n("5", "runCode", "Build Visual Pipeline Report", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build HTML email report from pipeline data
const reportHtml = '<div style="font-family:Arial,sans-serif;max-width:640px;margin:0 auto;">' +
  '<h1 style="color:#1a1a2e;">Weekly Pipeline Snapshot</h1>' +
  '<p style="color:#666;font-size:14px;">' + vars.snapshot_headline + '</p>' +
  '<div style="display:flex;gap:16px;margin:20px 0;">' +
  '<div style="background:#f0f4ff;padding:16px;border-radius:8px;flex:1;text-align:center;"><strong>' + vars.total_deals + '</strong><br/><small>Total Deals</small></div>' +
  '<div style="background:#f0fff4;padding:16px;border-radius:8px;flex:1;text-align:center;"><strong>' + vars.total_revenue + '</strong><br/><small>Pipeline Value</small></div>' +
  '<div style="background:#fff4f0;padding:16px;border-radius:8px;flex:1;text-align:center;"><strong>' + vars.weighted_revenue + '</strong><br/><small>Weighted Value</small></div>' +
  '</div>' +
  '<div style="margin:20px 0;"><strong>Stage Distribution:</strong><br/>' + (vars.stage_breakdown || "").split(" | ").join("<br/>") + '</div>' +
  '<h3>Key Insights</h3><ul>' +
  '<li>' + vars.insight_1 + '</li>' +
  '<li>' + vars.insight_2 + '</li>' +
  '<li>' + vars.insight_3 + '</li></ul>' +
  (vars.risk_alert ? '<div style="background:#fff3cd;padding:12px;border-radius:6px;margin:16px 0;"><strong>Risk Alert:</strong> ' + vars.risk_alert + '</div>' : '') +
  '<div style="background:#d4edda;padding:12px;border-radius:6px;"><strong>Top Recommendation:</strong> ' + vars.top_recommendation + '</div>' +
  '</div>';
return { report_html: reportHtml, report_date: new Date().toISOString().split("T")[0], report_subject: "Pipeline Snapshot: " + vars.total_revenue + " across " + vars.total_deals + " deals" };`,
          outputVariables: [
            { name: "report_html", type: "string" },
            { name: "report_date", type: "string" },
            { name: "report_subject", type: "string" },
          ],
        },
      }),

      // 6. Snapshot email distribution
      emailNode(
        "6",
        "Send Pipeline Snapshot Email",
        1240,
        "{sales_distribution_list}",
        "{report_subject}",
        "{report_html}",
      ),

      // 7. CRM snapshot archive Sheets
      sheetsNode(
        "7",
        "Archive Snapshot to Sheets",
        1480,
        "PipelineSnapshots",
        '[["{report_date}","{total_deals}","{total_revenue}","{weighted_revenue}","{avg_deal_size}","{closing_this_month}","{new_this_week}","{pipeline_health}","{snapshot_headline}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1672. SALES PLAYBOOK CHATBOT (8 nodes)
  // ===================================================================

  "Sales Playbook Chatbot": {
    summary:
      "Fires when a rep messages the Slack playbook channel via webhook, classifies intent with AI, retrieves playbook knowledge from an API, integrates CRM context from HubSpot, generates an AI response, posts an interactive follow-up to Slack, links related documents with runCode, and logs usage analytics to Sheets.",
    nodes: [
      trigger("Webhook: Slack playbook channel message", "webhook_app", {
        source: "slack",
        event: "message.channels",
        howItStarts:
          "Fires when a sales rep posts a question in the #sales-playbook Slack channel — requesting guidance on methodology, process, pricing, competitive positioning, or deal strategy",
        setup:
          "Configure Slack Events API for messages in the sales-playbook channel. Set OpenAI API key for intent classification and response generation. Provide HubSpot token for CRM context. Set Google Sheets credentials for usage analytics.",
        inputFields: [],
      }),

      // 2. AI intent classification
      aiNode(
        "2",
        "AI Classify Playbook Intent",
        280,
        "You are a sales playbook assistant. From the raw Slack message webhook, extract the rep name, user ID, question text, any mentioned deal or prospect, and classify the intent into a playbook category (methodology, pricing, competitive, discovery, demo, negotiation, closing, onboarding, territory, forecasting). Respond JSON: {rep_name: string, rep_user_id: string, question: string, deal_mentioned: string, prospect_mentioned: string, playbook_category: string, subcategory: string, urgency: string, channel_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract and classify the playbook question regardless of field naming conventions.",
        [
          { field: "rep_name", as: "rep_name", type: "string" },
          { field: "rep_user_id", as: "rep_user_id", type: "string" },
          { field: "question", as: "question", type: "string" },
          { field: "deal_mentioned", as: "deal_mentioned", type: "string" },
          {
            field: "prospect_mentioned",
            as: "prospect_mentioned",
            type: "string",
          },
          {
            field: "playbook_category",
            as: "playbook_category",
            type: "string",
          },
          { field: "subcategory", as: "subcategory", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
          { field: "channel_id", as: "channel_id", type: "string" },
        ],
      ),

      // 3. Playbook knowledge retrieval API
      n("3", "hitApi", "Retrieve Playbook Knowledge", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/chat/completions",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "gpt-4o-mini" },
            {
              key: "messages",
              value:
                '[{"role":"system","content":"You are a sales playbook knowledge engine. Given a category and question, return the relevant playbook content including step-by-step processes, templates, scripts, and best practices. Respond JSON: {playbook_content: string, process_steps: string, template_text: string, best_practices: string, common_mistakes: string, related_topics: string}"},{"role":"user","content":"Category: {playbook_category}. Subcategory: {subcategory}. Question: {question}. Provide comprehensive playbook guidance."}]',
            },
            { key: "response_format", value: '{"type":"json_object"}' },
          ],
          variables: [
            {
              name: "choices[0].message.content.playbook_content",
              customName: "playbook_content",
              type: "string",
            },
            {
              name: "choices[0].message.content.process_steps",
              customName: "process_steps",
              type: "string",
            },
            {
              name: "choices[0].message.content.template_text",
              customName: "template_text",
              type: "string",
            },
            {
              name: "choices[0].message.content.best_practices",
              customName: "best_practices",
              type: "string",
            },
            {
              name: "choices[0].message.content.common_mistakes",
              customName: "common_mistakes",
              type: "string",
            },
            {
              name: "choices[0].message.content.related_topics",
              customName: "related_topics",
              type: "string",
            },
          ],
        },
      }),

      // 4. CRM context integration HubSpot
      hubspotGetNode(
        "4",
        "Fetch CRM Context for Deal",
        760,
        'crm/v3/objects/deals?limit=1&properties=dealname,amount,dealstage,pipeline,closedate,hubspot_owner_id&filterGroups=[{"filters":[{"propertyName":"dealname","operator":"CONTAINS_TOKEN","value":"{deal_mentioned}"}]}]',
        [
          {
            name: "results[0].properties.dealname",
            customName: "crm_deal_name",
            type: "string",
          },
          {
            name: "results[0].properties.amount",
            customName: "crm_deal_amount",
            type: "string",
          },
          {
            name: "results[0].properties.dealstage",
            customName: "crm_deal_stage",
            type: "string",
          },
          {
            name: "results[0].properties.closedate",
            customName: "crm_close_date",
            type: "string",
          },
          {
            name: "results[0].properties.pipeline",
            customName: "crm_pipeline",
            type: "string",
          },
        ],
      ),

      // 5. AI response generation
      aiNode(
        "5",
        "AI Generate Playbook Response",
        1000,
        "You are an expert sales enablement coach. Given a rep's question, the relevant playbook content, and CRM deal context, craft a clear, actionable answer. Tailor the advice to the specific deal stage if applicable. Include process steps, a template if relevant, and warnings about common mistakes. Respond JSON: {answer: string, tailored_steps: string, template_snippet: string, watch_out: string, follow_up_suggestion: string}",
        "Rep {rep_name} asks: {question}. Category: {playbook_category}/{subcategory}. Deal: {crm_deal_name} (${crm_deal_amount}) at {crm_deal_stage} stage, closing {crm_close_date}. Playbook: {playbook_content}. Steps: {process_steps}. Template: {template_text}. Best practices: {best_practices}. Common mistakes: {common_mistakes}. Generate tailored playbook response.",
        [
          { field: "answer", as: "playbook_answer", type: "string" },
          { field: "tailored_steps", as: "tailored_steps", type: "string" },
          { field: "template_snippet", as: "template_snippet", type: "string" },
          { field: "watch_out", as: "watch_out", type: "string" },
          {
            field: "follow_up_suggestion",
            as: "follow_up_suggestion",
            type: "string",
          },
        ],
      ),

      // 6. Interactive follow-up Slack
      slackNode(
        "6",
        "Post Playbook Response to Slack",
        1240,
        "<@{rep_user_id}> *Playbook: {playbook_category}*\\n\\n{playbook_answer}\\n\\n*Steps:*\\n{tailored_steps}\\n\\n*Template:*\\n```{template_snippet}```\\n\\n*Watch Out:*\\n{watch_out}\\n\\n_Related: {related_topics}_\\n_Suggestion: {follow_up_suggestion}_",
      ),

      // 7. Document linking runCode
      n("7", "runCode", "Prepare Document Links", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build document links from related topics and category
const category = vars.playbook_category || "general";
const related = (vars.related_topics || "").split(",").map(t => t.trim()).filter(Boolean);
const docLinks = {
  methodology: "https://docs.company.com/playbook/methodology",
  pricing: "https://docs.company.com/playbook/pricing-guide",
  competitive: "https://docs.company.com/playbook/battle-cards",
  discovery: "https://docs.company.com/playbook/discovery-framework",
  demo: "https://docs.company.com/playbook/demo-playbook",
  negotiation: "https://docs.company.com/playbook/negotiation-guide",
  closing: "https://docs.company.com/playbook/closing-techniques",
  onboarding: "https://docs.company.com/playbook/customer-onboarding",
  territory: "https://docs.company.com/playbook/territory-planning",
  forecasting: "https://docs.company.com/playbook/forecasting-guide"
};
const primaryLink = docLinks[category] || docLinks.methodology;
const relatedLinks = related.slice(0, 3).map(t => docLinks[t.toLowerCase()] || "").filter(Boolean).join(" | ");
return { primary_doc_link: primaryLink, related_doc_links: relatedLinks || "No additional docs", topic_count: String(related.length) };`,
          outputVariables: [
            { name: "primary_doc_link", type: "string" },
            { name: "related_doc_links", type: "string" },
            { name: "topic_count", type: "string" },
          ],
        },
      }),

      // 8. Usage analytics Sheets
      sheetsNode(
        "8",
        "Log Playbook Usage to Sheets",
        1720,
        "PlaybookUsage",
        '[["{rep_name}","{playbook_category}","{subcategory}","{question}","{deal_mentioned}","{urgency}","{primary_doc_link}","{topic_count}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1673. SALES PROPOSAL GENERATOR (9 nodes)
  // ===================================================================

  "Sales Proposal Generator": {
    summary:
      "Fires when a deal reaches proposal stage via webhook, extracts opportunity data from HubSpot, enriches customer research via Clearbit API, generates AI proposal content, assembles pricing terms with runCode, builds the proposal document with runCode, routes for internal review via approval gate, delivers the proposal via email, and updates CRM status in HubSpot.",
    nodes: [
      trigger("Webhook: Deal reaches proposal stage", "webhook_app", {
        source: "hubspot",
        event: "deal.propertyChange.dealstage",
        howItStarts:
          "Fires when a deal transitions to the proposal/presentation stage in the CRM — triggering automated proposal generation with customer-specific content, pricing, and competitive positioning",
        setup:
          "Configure HubSpot webhook for deal stage change to proposal. Set OpenAI API key for content generation. Provide Clearbit API key for customer enrichment. Configure email credentials for proposal delivery. Set HubSpot token for CRM updates.",
        inputFields: [],
      }),

      // 2. Opportunity data extraction HubSpot
      aiNode(
        "2",
        "AI Extract Proposal Trigger Data",
        280,
        "You are a CRM deal stage change parser. From the raw webhook payload, extract the deal ID, deal name, deal amount, deal stage, contact email, contact name, contact title, company name, deal owner email, pipeline name, close date, and any deal notes or description. Respond JSON: {deal_id: string, deal_name: string, deal_amount: string, deal_stage: string, contact_email: string, contact_name: string, contact_title: string, company_name: string, owner_email: string, pipeline: string, close_date: string, deal_description: string}",
        "Raw webhook payload: {webhook_payload}. Extract all proposal-stage deal data regardless of field naming conventions.",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "contact_title", as: "contact_title", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "owner_email", as: "owner_email", type: "string" },
          { field: "pipeline", as: "pipeline", type: "string" },
          { field: "close_date", as: "close_date", type: "string" },
          { field: "deal_description", as: "deal_description", type: "string" },
        ],
      ),

      // 3. Customer research enrichment Clearbit API
      n("3", "hitApi", "Enrich Customer via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_name}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name",
              customName: "enriched_company_name",
              type: "string",
            },
            {
              name: "metrics.employees",
              customName: "company_employees",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "company_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "company_industry",
              type: "string",
            },
            {
              name: "category.sector",
              customName: "company_sector",
              type: "string",
            },
            {
              name: "description",
              customName: "company_description",
              type: "string",
            },
          ],
        },
      }),

      // 4. AI proposal content generator
      aiNode(
        "4",
        "AI Generate Proposal Content",
        760,
        "You are a professional proposal writer for B2B SaaS. Given the deal context, customer profile, and company details, generate a comprehensive proposal including an executive summary, value proposition tailored to their industry, key benefits, implementation timeline, and success metrics. Respond JSON: {executive_summary: string, value_proposition: string, key_benefits: string, implementation_timeline: string, success_metrics: string, competitive_differentiators: string, risk_mitigation: string}",
        "Deal: {deal_name} for ${deal_amount}. Contact: {contact_name} ({contact_title}) at {enriched_company_name}. Industry: {company_industry}/{company_sector}. Employees: {company_employees}. Revenue: ${company_revenue}. Description: {company_description}. Deal notes: {deal_description}. Close target: {close_date}. Generate tailored proposal content.",
        [
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          {
            field: "value_proposition",
            as: "value_proposition",
            type: "string",
          },
          { field: "key_benefits", as: "key_benefits", type: "string" },
          {
            field: "implementation_timeline",
            as: "implementation_timeline",
            type: "string",
          },
          { field: "success_metrics", as: "success_metrics", type: "string" },
          {
            field: "competitive_differentiators",
            as: "competitive_differentiators",
            type: "string",
          },
          { field: "risk_mitigation", as: "risk_mitigation", type: "string" },
        ],
      ),

      // 5. Pricing terms assembly runCode
      n("5", "runCode", "Assemble Pricing and Terms", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build pricing section from deal amount and company context
const dealAmount = parseFloat(vars.deal_amount) || 0;
const employees = parseInt(vars.company_employees) || 50;
let tier = "Starter";
let annualDiscount = "10%";
let paymentTerms = "Net 30";
if (dealAmount >= 100000) { tier = "Enterprise"; annualDiscount = "20%"; paymentTerms = "Net 45"; }
else if (dealAmount >= 50000) { tier = "Business"; annualDiscount = "15%"; paymentTerms = "Net 30"; }
else if (dealAmount >= 20000) { tier = "Professional"; annualDiscount = "12%"; paymentTerms = "Net 30"; }
const monthlyPrice = Math.round(dealAmount / 12);
const annualPrice = dealAmount;
const discountedAnnual = Math.round(annualPrice * (1 - parseInt(annualDiscount) / 100));
const implementationFee = Math.round(dealAmount * 0.15);
const totalFirstYear = discountedAnnual + implementationFee;
const pricingSection = "Tier: " + tier + " | Monthly: $" + monthlyPrice.toLocaleString() + " | Annual: $" + annualPrice.toLocaleString() + " (save " + annualDiscount + " = $" + discountedAnnual.toLocaleString() + ") | Implementation: $" + implementationFee.toLocaleString() + " | Total Year 1: $" + totalFirstYear.toLocaleString();
return {
  pricing_tier: tier,
  monthly_price: "$" + monthlyPrice.toLocaleString(),
  annual_price: "$" + annualPrice.toLocaleString(),
  annual_discount: annualDiscount,
  discounted_annual: "$" + discountedAnnual.toLocaleString(),
  implementation_fee: "$" + implementationFee.toLocaleString(),
  total_first_year: "$" + totalFirstYear.toLocaleString(),
  payment_terms: paymentTerms,
  pricing_section: pricingSection
};`,
          outputVariables: [
            { name: "pricing_tier", type: "string" },
            { name: "monthly_price", type: "string" },
            { name: "annual_price", type: "string" },
            { name: "annual_discount", type: "string" },
            { name: "discounted_annual", type: "string" },
            { name: "implementation_fee", type: "string" },
            { name: "total_first_year", type: "string" },
            { name: "payment_terms", type: "string" },
            { name: "pricing_section", type: "string" },
          ],
        },
      }),

      // 6. Proposal document builder runCode
      n("6", "runCode", "Build Proposal Document HTML", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build full proposal HTML document
const html = '<div style="font-family:Georgia,serif;max-width:800px;margin:0 auto;padding:40px;">' +
  '<div style="text-align:center;border-bottom:3px solid #1a1a2e;padding-bottom:30px;">' +
  '<h1 style="color:#1a1a2e;margin-bottom:5px;">Business Proposal</h1>' +
  '<h2 style="color:#555;">' + vars.deal_name + '</h2>' +
  '<p style="color:#888;">Prepared for ' + vars.contact_name + ' at ' + vars.enriched_company_name + '</p></div>' +
  '<h3>Executive Summary</h3><p>' + vars.executive_summary + '</p>' +
  '<h3>Value Proposition</h3><p>' + vars.value_proposition + '</p>' +
  '<h3>Key Benefits</h3><p>' + vars.key_benefits + '</p>' +
  '<h3>Competitive Differentiators</h3><p>' + vars.competitive_differentiators + '</p>' +
  '<h3>Implementation Timeline</h3><p>' + vars.implementation_timeline + '</p>' +
  '<h3>Success Metrics</h3><p>' + vars.success_metrics + '</p>' +
  '<h3>Investment</h3>' +
  '<table style="width:100%;border-collapse:collapse;margin:20px 0;">' +
  '<tr style="background:#f0f4ff;"><td style="padding:10px;border:1px solid #ddd;">Plan</td><td style="padding:10px;border:1px solid #ddd;">' + vars.pricing_tier + '</td></tr>' +
  '<tr><td style="padding:10px;border:1px solid #ddd;">Annual Investment</td><td style="padding:10px;border:1px solid #ddd;">' + vars.discounted_annual + ' (' + vars.annual_discount + ' discount)</td></tr>' +
  '<tr style="background:#f0f4ff;"><td style="padding:10px;border:1px solid #ddd;">Implementation</td><td style="padding:10px;border:1px solid #ddd;">' + vars.implementation_fee + '</td></tr>' +
  '<tr><td style="padding:10px;border:1px solid #ddd;font-weight:bold;">Total Year 1</td><td style="padding:10px;border:1px solid #ddd;font-weight:bold;">' + vars.total_first_year + '</td></tr>' +
  '<tr style="background:#f0f4ff;"><td style="padding:10px;border:1px solid #ddd;">Payment Terms</td><td style="padding:10px;border:1px solid #ddd;">' + vars.payment_terms + '</td></tr>' +
  '</table>' +
  '<h3>Risk Mitigation</h3><p>' + vars.risk_mitigation + '</p>' +
  '<div style="margin-top:40px;padding:20px;background:#f8f9fa;border-radius:8px;text-align:center;">' +
  '<p>Valid for 30 days from date of delivery. Target close: ' + vars.close_date + '</p></div></div>';
return { proposal_html: html, proposal_ready: "true" };`,
          outputVariables: [
            { name: "proposal_html", type: "string" },
            { name: "proposal_ready", type: "string" },
          ],
        },
      }),

      // 7. Internal review routing approval gate
      n("7", "approvalGate", "Internal Proposal Review", 1480, {
        approvalGateDetails: {
          approvers: ["{owner_email}"],
          message:
            "Please review the proposal for {deal_name} (${deal_amount}) to {contact_name} at {enriched_company_name}. Pricing: {pricing_tier} at {total_first_year} total Year 1.",
          timeoutHours: 24,
        },
      }),

      // 8. Proposal delivery email
      emailNode(
        "8",
        "Deliver Proposal to Prospect",
        1720,
        "{contact_email}",
        "Proposal: {deal_name} — {enriched_company_name} Partnership",
        "{proposal_html}",
      ),

      // 9. CRM status update HubSpot
      hubspotPostNode(
        "9",
        "Update Deal Status in HubSpot",
        1960,
        "crm/v3/objects/deals/{deal_id}",
        [
          {
            key: "properties",
            value:
              '{"dealstage":"presentationscheduled","notes_last_contacted":"Proposal sent to {contact_name} — {pricing_tier} plan at {total_first_year}","hs_next_step":"Await proposal response"}',
          },
        ],
        [
          { name: "id", customName: "updated_deal_id", type: "string" },
          {
            name: "properties.dealstage",
            customName: "new_deal_stage",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1674. SALES TOUCHED NO MEETING BOOKED (8 nodes)
  // ===================================================================

  "Sales Touched No Meeting Booked": {
    summary:
      "Fires on a daily schedule, analyzes engagement data from HubSpot, segments leads by engagement level via condition, generates AI re-engagement messaging, sends multi-channel follow-ups via email/SMS, creates a booking link via Calendly API, delivers rep coaching via Slack, and routes to lead recycling via condition.",
    nodes: [
      trigger("Schedule: Daily engaged-but-no-meeting audit", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Fires daily at 9 AM to identify contacts who have been engaged by sales (email opens, link clicks, replies) but have not booked a meeting — detecting stalled outreach sequences",
        setup:
          "Set HubSpot API token for engagement analysis. Set OpenAI API key for message generation. Configure email and SMS credentials. Provide Calendly API key for booking link generation. Set Slack webhook for rep alerts.",
        inputFields: [],
      }),

      // 2. Engagement analysis HubSpot
      hubspotGetNode(
        "2",
        "Analyze Contact Engagement in HubSpot",
        280,
        'crm/v3/objects/contacts?limit=50&properties=email,firstname,lastname,company,jobtitle,hs_lead_status,num_contacted_times,notes_last_contacted,hs_email_last_open_date,hs_email_last_click_date,hs_sales_email_last_replied,hubspot_owner_id&filterGroups=[{"filters":[{"propertyName":"num_contacted_times","operator":"GTE","value":"2"},{"propertyName":"hs_email_last_open_date","operator":"HAS_PROPERTY"}]}]',
        [
          {
            name: "total",
            customName: "total_engaged_contacts",
            type: "number",
          },
          { name: "results", customName: "engaged_contacts", type: "string" },
        ],
      ),

      // 3. Engagement-based segmentation condition
      n("3", "condition", "Has Contact Opened but Not Replied?", 520, {
        conditionDetails: {
          conditions: [
            { variable: "total_engaged_contacts", operator: "gt", value: "0" },
          ],
        },
      }),

      // 4. AI re-engagement message
      aiNode(
        "4",
        "AI Generate Re-Engagement Message",
        760,
        "You are a sales re-engagement specialist. Given a contact who has opened/clicked emails but never booked a meeting, create a short, personalized follow-up message that acknowledges their interest without being pushy. Include both an email version and a brief SMS version. Respond JSON: {email_subject: string, email_body_html: string, sms_message: string, follow_up_angle: string, booking_cta: string}",
        "Contact data from HubSpot: {engaged_contacts}. Total engaged: {total_engaged_contacts}. These contacts have been touched multiple times and show engagement (opens/clicks) but have not booked a meeting. Create compelling but non-aggressive re-engagement messages with a clear CTA to book time.",
        [
          {
            field: "email_subject",
            as: "reengagement_subject",
            type: "string",
          },
          {
            field: "email_body_html",
            as: "reengagement_email",
            type: "string",
          },
          { field: "sms_message", as: "reengagement_sms", type: "string" },
          { field: "follow_up_angle", as: "follow_up_angle", type: "string" },
          { field: "booking_cta", as: "booking_cta", type: "string" },
        ],
      ),

      // 5. Multi-channel follow-up email
      emailNode(
        "5",
        "Send Re-Engagement Email",
        1000,
        "{contact_email}",
        "{reengagement_subject}",
        "{reengagement_email}",
      ),

      // 6. Simplified booking link Calendly API
      n("6", "hitApi", "Generate Calendly Booking Link", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduling_links",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "max_event_count", value: "1" },
            { key: "owner", value: "{calendly_event_uri}" },
            { key: "owner_type", value: "EventType" },
          ],
          variables: [
            {
              name: "resource.booking_url",
              customName: "booking_url",
              type: "string",
            },
          ],
        },
      }),

      // 7. Rep coaching notification Slack
      slackNode(
        "7",
        "Notify Rep of Stalled Engagement",
        1480,
        "Stalled Engagement Alert\\n\\n{total_engaged_contacts} contacts have opened/clicked but never booked a meeting.\\n\\n*Re-Engagement Angle:* {follow_up_angle}\\n*CTA:* {booking_cta}\\n*Booking Link:* {booking_url}\\n\\nReview and personalize outreach for high-priority contacts.",
      ),

      // 8. Lead recycling decision condition
      n("8", "condition", "Should Recycle to Marketing?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "total_engaged_contacts", operator: "gt", value: "10" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1675. SALESFORCE LEAD TO GOOGLE SHEET SYNC (7 nodes)
  // ===================================================================

  "Salesforce Lead to Google Sheet Sync": {
    summary:
      "Fires when a lead event occurs via webhook, transforms field mapping with runCode, checks for duplicates in Sheets API, routes via upsert logic condition, writes to Google Sheets, handles sync conflicts with runCode, and logs sync status to a dashboard Sheet.",
    nodes: [
      trigger("Webhook: Salesforce lead event", "webhook_app", {
        source: "salesforce",
        event: "lead.created_or_updated",
        howItStarts:
          "Fires when a new lead is created or an existing lead is updated in Salesforce — including all standard and custom lead fields for syncing to Google Sheets",
        setup:
          "Configure Salesforce webhook for lead create/update events. Set Google Sheets API credentials. Provide Google spreadsheet ID for the sync target. Set Slack webhook for error notifications.",
        inputFields: [],
      }),

      // 2. Field mapping transformer runCode
      n("2", "runCode", "Transform Lead Field Mapping", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook and map Salesforce fields to Sheet columns
let payload;
try { payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload; } catch(e) { payload = {}; }
const record = payload.new || payload.record || payload;
const eventType = payload.event || payload.type || "unknown";
const leadId = record.Id || record.id || "";
const email = record.Email || record.email || "";
const firstName = record.FirstName || record.first_name || "";
const lastName = record.LastName || record.last_name || "";
const company = record.Company || record.company || "";
const title = record.Title || record.title || "";
const phone = record.Phone || record.phone || "";
const status = record.Status || record.status || "";
const source = record.LeadSource || record.lead_source || "";
const industry = record.Industry || record.industry || "";
const rating = record.Rating || record.rating || "";
const state = record.State || record.state || "";
const createdDate = record.CreatedDate || record.created_date || new Date().toISOString();
return {
  sf_lead_id: leadId, sf_email: email, sf_first_name: firstName, sf_last_name: lastName,
  sf_company: company, sf_title: title, sf_phone: phone, sf_status: status,
  sf_source: source, sf_industry: industry, sf_rating: rating, sf_state: state,
  sf_created_date: createdDate, sync_event_type: eventType,
  sheet_row: '["' + [leadId, email, firstName, lastName, company, title, phone, status, source, industry, rating, state, createdDate].join('","') + '"]'
};`,
          outputVariables: [
            { name: "sf_lead_id", type: "string" },
            { name: "sf_email", type: "string" },
            { name: "sf_first_name", type: "string" },
            { name: "sf_last_name", type: "string" },
            { name: "sf_company", type: "string" },
            { name: "sf_title", type: "string" },
            { name: "sf_phone", type: "string" },
            { name: "sf_status", type: "string" },
            { name: "sf_source", type: "string" },
            { name: "sf_industry", type: "string" },
            { name: "sf_rating", type: "string" },
            { name: "sf_state", type: "string" },
            { name: "sf_created_date", type: "string" },
            { name: "sync_event_type", type: "string" },
            { name: "sheet_row", type: "string" },
          ],
        },
      }),

      // 3. Duplicate detection Sheets API
      n("3", "hitApi", "Check for Duplicate in Google Sheets", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/SalesforceLeads!A:A",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "values", customName: "existing_ids", type: "string" },
          ],
        },
      }),

      // 4. Upsert logic condition
      n("4", "condition", "Is This a New Lead or Update?", 760, {
        conditionDetails: {
          conditions: [
            {
              variable: "sync_event_type",
              operator: "eq",
              value: "lead.created",
            },
          ],
        },
      }),

      // 5. Google Sheet write
      sheetsNode(
        "5",
        "Write Lead to Google Sheet",
        1000,
        "SalesforceLeads",
        '[["{sf_lead_id}","{sf_email}","{sf_first_name}","{sf_last_name}","{sf_company}","{sf_title}","{sf_phone}","{sf_status}","{sf_source}","{sf_industry}","{sf_rating}","{sf_state}","{sf_created_date}"]]',
      ),

      // 6. Sync conflict handler runCode
      n("6", "runCode", "Handle Sync Conflicts", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check for sync conflicts and log resolution
const eventType = vars.sync_event_type || "unknown";
const rowsLogged = parseInt(vars.rows_logged) || 0;
const syncSuccess = rowsLogged > 0 ? "true" : "false";
const conflictType = eventType === "lead.updated" ? "update_overwrite" : "new_insert";
const resolution = syncSuccess ? "completed" : "failed_retry_needed";
const timestamp = new Date().toISOString();
return {
  sync_success: syncSuccess,
  conflict_type: conflictType,
  conflict_resolution: resolution,
  sync_timestamp: timestamp,
  sync_summary: eventType + " for " + vars.sf_email + " — " + resolution
};`,
          outputVariables: [
            { name: "sync_success", type: "string" },
            { name: "conflict_type", type: "string" },
            { name: "conflict_resolution", type: "string" },
            { name: "sync_timestamp", type: "string" },
            { name: "sync_summary", type: "string" },
          ],
        },
      }),

      // 7. Sync status dashboard Sheets
      sheetsNode(
        "7",
        "Log Sync Status to Dashboard",
        1480,
        "SyncDashboard",
        '[["{sync_timestamp}","{sf_lead_id}","{sf_email}","{sync_event_type}","{conflict_type}","{conflict_resolution}","{sync_success}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1676. SALESFORCE OPPORTUNITY TO SLACK (7 nodes)
  // ===================================================================

  "Salesforce Opportunity to Slack": {
    summary:
      "Fires when a Salesforce opportunity changes via webhook, classifies the change type with runCode, enriches deal context from HubSpot, formats the notification with runCode, routes by channel via condition, delivers a Slack notification, and handles celebration events via condition.",
    nodes: [
      trigger("Webhook: Salesforce opportunity change", "webhook_app", {
        source: "salesforce",
        event: "opportunity.updated",
        howItStarts:
          "Fires when a Salesforce opportunity is created, updated, moved to a new stage, or closed — including stage changes, amount updates, close date changes, and owner transfers",
        setup:
          "Configure Salesforce webhook for opportunity change events. Set HubSpot API token for deal context enrichment. Configure Slack incoming webhook for notification delivery.",
        inputFields: [],
      }),

      // 2. Change type classifier runCode
      n("2", "runCode", "Classify Opportunity Change Type", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse and classify the opportunity change
let payload;
try { payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload; } catch(e) { payload = {}; }
const record = payload.new || payload.record || payload;
const oldRecord = payload.old || payload.previous || {};
const opp_id = record.Id || record.id || "";
const opp_name = record.Name || record.name || "";
const amount = record.Amount || record.amount || "0";
const stage = record.StageName || record.stage_name || "";
const oldStage = oldRecord.StageName || oldRecord.stage_name || "";
const closeDate = record.CloseDate || record.close_date || "";
const ownerName = record.OwnerName || record.owner_name || "";
const accountName = record.AccountName || record.account_name || "";
let changeType = "updated";
let isWon = "false";
let isLost = "false";
let isCelebration = "false";
if (stage !== oldStage && stage) {
  changeType = "stage_change";
  if (stage.toLowerCase().includes("closed won")) { changeType = "won"; isWon = "true"; isCelebration = "true"; }
  if (stage.toLowerCase().includes("closed lost")) { changeType = "lost"; isLost = "true"; }
}
if (!oldStage && stage) changeType = "created";
const amountNum = parseFloat(amount) || 0;
const isLargeDeal = amountNum >= 100000 ? "true" : "false";
const channel = isLargeDeal === "true" || isCelebration === "true" ? "#sales-wins" : "#sales-pipeline";
return { opp_id, opp_name, opp_amount: amount, opp_stage: stage, old_stage: oldStage, close_date: closeDate, owner_name: ownerName, account_name: accountName, change_type: changeType, is_won: isWon, is_lost: isLost, is_celebration: isCelebration, is_large_deal: isLargeDeal, target_channel: channel };`,
          outputVariables: [
            { name: "opp_id", type: "string" },
            { name: "opp_name", type: "string" },
            { name: "opp_amount", type: "string" },
            { name: "opp_stage", type: "string" },
            { name: "old_stage", type: "string" },
            { name: "close_date", type: "string" },
            { name: "owner_name", type: "string" },
            { name: "account_name", type: "string" },
            { name: "change_type", type: "string" },
            { name: "is_won", type: "string" },
            { name: "is_lost", type: "string" },
            { name: "is_celebration", type: "string" },
            { name: "is_large_deal", type: "string" },
            { name: "target_channel", type: "string" },
          ],
        },
      }),

      // 3. Deal context enrichment HubSpot
      hubspotGetNode(
        "3",
        "Enrich Deal Context from HubSpot",
        520,
        'crm/v3/objects/companies?limit=1&properties=name,industry,numberofemployees,annualrevenue,city,state&filterGroups=[{"filters":[{"propertyName":"name","operator":"CONTAINS_TOKEN","value":"{account_name}"}]}]',
        [
          {
            name: "results[0].properties.industry",
            customName: "account_industry",
            type: "string",
          },
          {
            name: "results[0].properties.numberofemployees",
            customName: "account_employees",
            type: "string",
          },
          {
            name: "results[0].properties.annualrevenue",
            customName: "account_revenue",
            type: "string",
          },
          {
            name: "results[0].properties.city",
            customName: "account_city",
            type: "string",
          },
        ],
      ),

      // 4. Notification formatting runCode
      n("4", "runCode", "Format Slack Notification", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build formatted Slack message based on change type
const type = vars.change_type || "updated";
const amount = parseFloat(vars.opp_amount) || 0;
const formattedAmount = "$" + amount.toLocaleString();
let emoji = "";
let headline = "";
if (type === "won") { emoji = "\\ud83c\\udfc6"; headline = "DEAL WON!"; }
else if (type === "lost") { emoji = "\\ud83d\\udea8"; headline = "Deal Lost"; }
else if (type === "created") { emoji = "\\ud83c\\udf1f"; headline = "New Opportunity"; }
else if (type === "stage_change") { emoji = "\\u27a1\\ufe0f"; headline = "Stage Change"; }
else { emoji = "\\ud83d\\udcdd"; headline = "Opportunity Updated"; }
const message = emoji + " *" + headline + ": " + vars.opp_name + "*\\n" +
  "Account: " + vars.account_name + " (" + (vars.account_industry || "N/A") + ")\\n" +
  "Amount: " + formattedAmount + " | Stage: " + (vars.old_stage ? vars.old_stage + " -> " : "") + vars.opp_stage + "\\n" +
  "Owner: " + vars.owner_name + " | Close: " + vars.close_date + "\\n" +
  "Employees: " + (vars.account_employees || "N/A") + " | Revenue: $" + (vars.account_revenue || "N/A");
return { formatted_message: message, notification_priority: type === "won" || type === "lost" ? "high" : "normal" };`,
          outputVariables: [
            { name: "formatted_message", type: "string" },
            { name: "notification_priority", type: "string" },
          ],
        },
      }),

      // 5. Channel routing logic condition
      n("5", "condition", "Is This a High-Priority Change?", 1000, {
        conditionDetails: {
          conditions: [
            {
              variable: "notification_priority",
              operator: "eq",
              value: "high",
            },
          ],
        },
      }),

      // 6. Slack notification delivery
      slackNode(
        "6",
        "Send Opportunity Alert to Slack",
        1240,
        "{formatted_message}",
      ),

      // 7. Celebration handler condition
      n("7", "condition", "Is This a Deal Won Celebration?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "is_celebration", operator: "eq", value: "true" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1677. SECURITY REVIEW FOLLOW-UP (8 nodes)
  // ===================================================================

  "Security Review Follow-Up": {
    summary:
      "Fires when a deal enters security review stage via webhook, assembles security documentation with runCode, pre-fills a security questionnaire with AI, creates a Jira ticket for the security team, sends a prospect acknowledgment email, waits 7 days, tracks progress in Slack, and handles review completion in HubSpot.",
    nodes: [
      trigger("Webhook: Deal enters security review stage", "webhook_app", {
        source: "hubspot",
        event: "deal.propertyChange.dealstage",
        howItStarts:
          "Fires when a deal transitions to the security review stage in the CRM — indicating the prospect's security/compliance team needs to evaluate the product before purchase approval",
        setup:
          "Configure HubSpot webhook for deal stage change to security review. Set OpenAI API key for questionnaire pre-fill. Provide Jira API credentials for ticket creation. Configure email credentials for prospect acknowledgment. Set Slack webhook for progress tracking.",
        inputFields: [],
      }),

      // 2. Security documentation assembly runCode
      n("2", "runCode", "Assemble Security Documentation Package", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook and prepare security doc package
let payload;
try { payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload; } catch(e) { payload = {}; }
const record = payload.properties || payload.record || payload;
const dealId = record.hs_object_id || payload.objectId || "";
const dealName = record.dealname || "";
const amount = record.amount || "0";
const contactEmail = record.contact_email || payload.contact_email || "";
const contactName = record.contact_name || payload.contact_name || "";
const companyName = record.company || payload.company_name || "";
const ownerEmail = record.hubspot_owner_id || payload.owner_email || "";
const securityDocs = [
  "SOC 2 Type II Report (current year)",
  "ISO 27001 Certificate",
  "Data Processing Agreement (DPA)",
  "Privacy Policy & GDPR Compliance",
  "Penetration Test Summary",
  "Business Continuity Plan",
  "Vendor Security Questionnaire (pre-filled)"
].join(", ");
const docLinks = "https://trust.company.com/soc2 | https://trust.company.com/iso27001 | https://trust.company.com/dpa";
return {
  sec_deal_id: dealId, sec_deal_name: dealName, sec_deal_amount: amount,
  sec_contact_email: contactEmail, sec_contact_name: contactName,
  sec_company_name: companyName, sec_owner_email: ownerEmail,
  security_docs_list: securityDocs, security_doc_links: docLinks,
  review_started: new Date().toISOString().split("T")[0],
  review_deadline: new Date(Date.now() + 14 * 86400000).toISOString().split("T")[0]
};`,
          outputVariables: [
            { name: "sec_deal_id", type: "string" },
            { name: "sec_deal_name", type: "string" },
            { name: "sec_deal_amount", type: "string" },
            { name: "sec_contact_email", type: "string" },
            { name: "sec_contact_name", type: "string" },
            { name: "sec_company_name", type: "string" },
            { name: "sec_owner_email", type: "string" },
            { name: "security_docs_list", type: "string" },
            { name: "security_doc_links", type: "string" },
            { name: "review_started", type: "string" },
            { name: "review_deadline", type: "string" },
          ],
        },
      }),

      // 3. AI questionnaire pre-fill
      aiNode(
        "3",
        "AI Pre-Fill Security Questionnaire",
        520,
        "You are a security compliance specialist. Given a company name and deal context, pre-fill common security questionnaire answers for a SaaS product. Cover data encryption (at rest AES-256, in transit TLS 1.3), access controls (RBAC, MFA, SSO), data residency, backup/DR, incident response, and employee security training. Respond JSON: {encryption_answer: string, access_control_answer: string, data_residency_answer: string, backup_dr_answer: string, incident_response_answer: string, compliance_certs: string, pre_filled_summary: string}",
        "Company: {sec_company_name}. Deal: {sec_deal_name} (${sec_deal_amount}). Pre-fill the standard security questionnaire with our compliance information for their review.",
        [
          {
            field: "encryption_answer",
            as: "encryption_answer",
            type: "string",
          },
          {
            field: "access_control_answer",
            as: "access_control_answer",
            type: "string",
          },
          {
            field: "data_residency_answer",
            as: "data_residency_answer",
            type: "string",
          },
          { field: "backup_dr_answer", as: "backup_dr_answer", type: "string" },
          {
            field: "incident_response_answer",
            as: "incident_response_answer",
            type: "string",
          },
          { field: "compliance_certs", as: "compliance_certs", type: "string" },
          {
            field: "pre_filled_summary",
            as: "pre_filled_summary",
            type: "string",
          },
        ],
      ),

      // 4. Security team assignment Jira API
      n("4", "hitApi", "Create Jira Ticket for Security Team", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://your-domain.atlassian.net/rest/api/3/issue",
          headers: [
            { key: "Authorization", value: "Basic {jira_auth_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "fields",
              value:
                '{"project":{"key":"SEC"},"summary":"Security Review: {sec_deal_name} — {sec_company_name}","description":{"type":"doc","version":1,"content":[{"type":"paragraph","content":[{"type":"text","text":"Security review requested for deal {sec_deal_name} (${ sec_deal_amount}). Company: {sec_company_name}. Contact: {sec_contact_name} ({sec_contact_email}). Deadline: {review_deadline}. Pre-filled summary: {pre_filled_summary}"}]}]},"issuetype":{"name":"Task"},"priority":{"name":"High"},"duedate":"{review_deadline}"}',
            },
          ],
          variables: [
            { name: "id", customName: "jira_ticket_id", type: "string" },
            { name: "key", customName: "jira_ticket_key", type: "string" },
          ],
        },
      }),

      // 5. Prospect acknowledgment email
      emailNode(
        "5",
        "Send Security Review Acknowledgment",
        1000,
        "{sec_contact_email}",
        "Security Review Initiated — {sec_deal_name}",
        "<p>Hi {sec_contact_name},</p><p>Thank you for moving forward with the security review for <strong>{sec_deal_name}</strong>. We take your security evaluation seriously and have prepared the following documentation for your team:</p><ul><li>SOC 2 Type II Report</li><li>ISO 27001 Certificate</li><li>Data Processing Agreement</li><li>Pre-filled Security Questionnaire</li><li>Penetration Test Summary</li></ul><p>Access all documents at our Trust Center: {security_doc_links}</p><p>Our security team has been assigned to support your review. Please don't hesitate to reach out with any questions. We aim to complete all reviews within 14 business days (by {review_deadline}).</p><p>Best regards,<br/>Security & Compliance Team</p>",
      ),

      // 6. Delay 7 days
      n("6", "delayAction", "Wait 7 Days for Review Progress", 1240, {
        delayDetails: { duration: 7, unit: "days" },
      }),

      // 7. Internal progress tracker Slack
      slackNode(
        "7",
        "Track Security Review Progress in Slack",
        1480,
        "Security Review Progress Check\\n\\nDeal: *{sec_deal_name}* (${sec_deal_amount})\\nCompany: {sec_company_name}\\nContact: {sec_contact_name}\\nStarted: {review_started} | Deadline: {review_deadline}\\nJira: {jira_ticket_key}\\n\\nThis review has been in progress for 7 days. Please update the Jira ticket with current status.\\nDocs sent: {security_docs_list}",
      ),

      // 8. Review completion handler HubSpot
      hubspotPostNode(
        "8",
        "Update Deal with Review Status",
        1720,
        "crm/v3/objects/deals/{sec_deal_id}",
        [
          {
            key: "properties",
            value:
              '{"hs_next_step":"Security review in progress — Jira {jira_ticket_key}. Deadline: {review_deadline}","notes_last_contacted":"Security docs sent to {sec_contact_name}. Review started {review_started}."}',
          },
        ],
        [{ name: "id", customName: "updated_deal_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1678. SELF-SERVE TO ENTERPRISE HANDOFF (9 nodes)
  // ===================================================================

  "Self-Serve to Enterprise Handoff": {
    summary:
      "Fires when a self-serve account crosses usage thresholds via webhook, aggregates account intelligence from HubSpot, scores enterprise readiness with runCode, personalizes AI outreach, alerts the product-led sales team via Slack, sends a warm outreach email, triggers an in-app notification via API, creates a CRM opportunity in HubSpot, and monitors handoff progress with a 7-day delay.",
    nodes: [
      trigger("Webhook: Account crosses usage threshold", "webhook_app", {
        source: "product_analytics",
        event: "account.usage_threshold_reached",
        howItStarts:
          "Fires when a self-serve account crosses enterprise-qualifying thresholds — such as exceeding seat limits, API call volumes, storage quotas, or feature usage patterns that indicate enterprise needs",
        setup:
          "Configure product analytics webhook for threshold events. Set HubSpot API token for account intelligence. Set OpenAI API key for outreach personalization. Configure Slack, email, and in-app notification channels.",
        inputFields: [],
      }),

      // 2. Account intelligence aggregation HubSpot
      aiNode(
        "2",
        "AI Extract Usage Threshold Data",
        280,
        "You are a product analytics parser. From the raw webhook payload, extract the account ID, company name, primary contact email, primary contact name, current plan, seats used, seats limit, API calls this month, storage used, feature flags active, monthly spend, account created date, and the specific threshold that was exceeded. Respond JSON: {account_id: string, company_name: string, contact_email: string, contact_name: string, current_plan: string, seats_used: string, seats_limit: string, api_calls: string, storage_used_gb: string, active_features: string, monthly_spend: string, account_age_days: string, threshold_exceeded: string, threshold_value: string}",
        "Raw webhook payload: {webhook_payload}. Extract all usage threshold data regardless of field naming conventions.",
        [
          { field: "account_id", as: "account_id", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "current_plan", as: "current_plan", type: "string" },
          { field: "seats_used", as: "seats_used", type: "string" },
          { field: "seats_limit", as: "seats_limit", type: "string" },
          { field: "api_calls", as: "api_calls", type: "string" },
          { field: "storage_used_gb", as: "storage_used_gb", type: "string" },
          { field: "active_features", as: "active_features", type: "string" },
          { field: "monthly_spend", as: "monthly_spend", type: "string" },
          { field: "account_age_days", as: "account_age_days", type: "string" },
          {
            field: "threshold_exceeded",
            as: "threshold_exceeded",
            type: "string",
          },
          { field: "threshold_value", as: "threshold_value", type: "string" },
        ],
      ),

      // 3. Enterprise readiness scoring runCode
      n("3", "runCode", "Score Enterprise Readiness", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate enterprise readiness score from usage patterns
const seats = parseInt(vars.seats_used) || 0;
const apiCalls = parseInt(vars.api_calls) || 0;
const storage = parseFloat(vars.storage_used_gb) || 0;
const spend = parseFloat(vars.monthly_spend) || 0;
const ageDays = parseInt(vars.account_age_days) || 0;
const features = (vars.active_features || "").split(",").length;
let score = 0;
if (seats >= 20) score += 25; else if (seats >= 10) score += 15; else if (seats >= 5) score += 8;
if (apiCalls >= 50000) score += 20; else if (apiCalls >= 10000) score += 12;
if (storage >= 50) score += 15; else if (storage >= 20) score += 8;
if (spend >= 500) score += 20; else if (spend >= 200) score += 12;
if (ageDays >= 90) score += 10; else if (ageDays >= 30) score += 5;
if (features >= 8) score += 10; else if (features >= 5) score += 5;
const readinessLevel = score >= 80 ? "highly_ready" : score >= 50 ? "ready" : score >= 30 ? "emerging" : "early";
const estimatedACV = Math.max(spend * 12 * 3, 10000);
const urgency = score >= 70 ? "high" : score >= 40 ? "medium" : "low";
return {
  readiness_score: String(score),
  readiness_level: readinessLevel,
  estimated_acv: "$" + Math.round(estimatedACV).toLocaleString(),
  handoff_urgency: urgency,
  expansion_signal: vars.threshold_exceeded + " exceeded at " + vars.threshold_value
};`,
          outputVariables: [
            { name: "readiness_score", type: "string" },
            { name: "readiness_level", type: "string" },
            { name: "estimated_acv", type: "string" },
            { name: "handoff_urgency", type: "string" },
            { name: "expansion_signal", type: "string" },
          ],
        },
      }),

      // 4. AI outreach personalization
      aiNode(
        "4",
        "AI Personalize Enterprise Outreach",
        760,
        "You are a product-led growth sales specialist. Given a self-serve account that has crossed enterprise thresholds, craft a personalized outreach message that acknowledges their current usage, highlights enterprise features they would benefit from (SSO, RBAC, dedicated support, SLA, custom integrations), and offers a conversation without being pushy. Respond JSON: {email_subject: string, email_body_html: string, slack_summary: string, value_prop: string, enterprise_features: string}",
        "Company: {company_name}. Contact: {contact_name} ({contact_email}). Plan: {current_plan}. Seats: {seats_used}/{seats_limit}. API calls: {api_calls}. Storage: {storage_used_gb}GB. Monthly spend: ${monthly_spend}. Features: {active_features}. Threshold exceeded: {threshold_exceeded}. Readiness: {readiness_level} ({readiness_score}/100). Est. ACV: {estimated_acv}. Craft personalized enterprise upgrade outreach.",
        [
          { field: "email_subject", as: "outreach_subject", type: "string" },
          { field: "email_body_html", as: "outreach_body", type: "string" },
          { field: "slack_summary", as: "slack_summary", type: "string" },
          { field: "value_prop", as: "value_prop", type: "string" },
          {
            field: "enterprise_features",
            as: "enterprise_features",
            type: "string",
          },
        ],
      ),

      // 5. Product-led sales alert Slack
      slackNode(
        "5",
        "Alert PLS Team on Slack",
        1000,
        "Enterprise Handoff Opportunity\\n\\n*{company_name}* — {readiness_level} ({readiness_score}/100)\\nContact: {contact_name} ({contact_email})\\nCurrent plan: {current_plan} | Spend: ${monthly_spend}/mo\\nSeats: {seats_used}/{seats_limit} | API calls: {api_calls}\\nThreshold: {expansion_signal}\\nEst. ACV: {estimated_acv} | Urgency: {handoff_urgency}\\n\\n{slack_summary}\\n\\nEnterprise features to highlight: {enterprise_features}",
      ),

      // 6. Warm outreach email
      emailNode(
        "6",
        "Send Warm Enterprise Outreach Email",
        1240,
        "{contact_email}",
        "{outreach_subject}",
        "{outreach_body}",
      ),

      // 7. In-app notification API
      n("7", "hitApi", "Trigger In-App Enterprise Notification", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.company.com/v1/notifications",
          headers: [
            { key: "Authorization", value: "Bearer {internal_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "account_id", value: "{account_id}" },
            { key: "type", value: "enterprise_upgrade" },
            { key: "title", value: "Unlock Enterprise Features" },
            { key: "message", value: "{value_prop}" },
            {
              key: "cta_url",
              value: "https://app.company.com/enterprise-demo",
            },
          ],
          variables: [
            {
              name: "notification_id",
              customName: "in_app_notification_id",
              type: "string",
            },
            {
              name: "delivered",
              customName: "notification_delivered",
              type: "boolean",
            },
          ],
        },
      }),

      // 8. CRM opportunity creation HubSpot
      hubspotPostNode(
        "8",
        "Create Enterprise Opportunity in HubSpot",
        1720,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"{company_name} — Enterprise Upgrade","amount":"{estimated_acv}","dealstage":"qualifiedtobuy","pipeline":"default","closedate":"","hs_deal_stage_probability":"30","lead_source":"product_led_growth","notes_last_contacted":"Self-serve threshold exceeded: {expansion_signal}. Readiness: {readiness_level} ({readiness_score}/100). Current spend: ${monthly_spend}/mo."}',
          },
        ],
        [
          { name: "id", customName: "hs_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "created_deal_name",
            type: "string",
          },
        ],
      ),

      // 9. Handoff monitoring delay
      n("9", "delayAction", "Monitor Handoff Progress (7 days)", 1960, {
        delayDetails: { duration: 7, unit: "days" },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 1679. SLACK NEW DEAL ALERT (7 nodes)
  // ===================================================================

  "Slack New Deal Alert": {
    summary:
      "Fires when a new opportunity is created via webhook, enriches deal details from HubSpot, looks up company intelligence via Clearbit API, classifies priority via condition, formats the alert with runCode, posts a multi-channel Slack notification, and tracks response via condition.",
    nodes: [
      trigger("Webhook: New opportunity created", "webhook_app", {
        source: "hubspot",
        event: "deal.creation",
        howItStarts:
          "Fires when a new deal/opportunity is created in the CRM — including the deal name, amount, stage, assigned owner, and associated company information",
        setup:
          "Configure HubSpot webhook for deal creation events. Provide Clearbit API key for company intelligence. Configure Slack incoming webhook for alert delivery.",
        inputFields: [],
      }),

      // 2. Deal details enrichment HubSpot
      aiNode(
        "2",
        "AI Extract New Deal Data",
        280,
        "You are a CRM deal creation parser. From the raw webhook payload, extract the deal ID, deal name, deal amount, deal stage, pipeline, close date, owner email, owner name, contact email, contact name, company name, and deal source. Respond JSON: {deal_id: string, deal_name: string, deal_amount: string, deal_stage: string, pipeline: string, close_date: string, owner_email: string, owner_name: string, contact_email: string, contact_name: string, company_name: string, deal_source: string}",
        "Raw webhook payload: {webhook_payload}. Extract all new deal data regardless of field naming conventions.",
        [
          { field: "deal_id", as: "deal_id", type: "string" },
          { field: "deal_name", as: "deal_name", type: "string" },
          { field: "deal_amount", as: "deal_amount", type: "string" },
          { field: "deal_stage", as: "deal_stage", type: "string" },
          { field: "pipeline", as: "pipeline", type: "string" },
          { field: "close_date", as: "close_date", type: "string" },
          { field: "owner_email", as: "owner_email", type: "string" },
          { field: "owner_name", as: "owner_name", type: "string" },
          { field: "contact_email", as: "contact_email", type: "string" },
          { field: "contact_name", as: "contact_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          { field: "deal_source", as: "deal_source", type: "string" },
        ],
      ),

      // 3. Company intelligence lookup Clearbit API
      n("3", "hitApi", "Lookup Company via Clearbit", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://company.clearbit.com/v2/companies/find?domain={company_name}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            { name: "name", customName: "cb_company_name", type: "string" },
            {
              name: "metrics.employees",
              customName: "cb_employees",
              type: "string",
            },
            {
              name: "metrics.annualRevenue",
              customName: "cb_revenue",
              type: "string",
            },
            {
              name: "category.industry",
              customName: "cb_industry",
              type: "string",
            },
            { name: "geo.country", customName: "cb_country", type: "string" },
            { name: "tech", customName: "cb_tech_stack", type: "string" },
          ],
        },
      }),

      // 4. Priority classification condition
      n("4", "condition", "Is This a High-Value Deal?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "deal_amount", operator: "gt", value: "50000" },
          ],
        },
      }),

      // 5. Slack alert formatting runCode
      n("5", "runCode", "Format New Deal Slack Alert", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format the new deal alert for Slack
const amount = parseFloat(vars.deal_amount) || 0;
const employees = parseInt(vars.cb_employees) || 0;
const isEnterprise = amount >= 100000 || employees >= 500;
const emoji = isEnterprise ? "\\ud83d\\ude80" : amount >= 50000 ? "\\ud83c\\udf1f" : "\\ud83d\\udcbc";
const tier = isEnterprise ? "ENTERPRISE" : amount >= 50000 ? "MID-MARKET" : "SMB";
const message = emoji + " *NEW DEAL: " + vars.deal_name + "* [" + tier + "]\\n\\n" +
  "*Amount:* $" + amount.toLocaleString() + "\\n" +
  "*Company:* " + (vars.cb_company_name || vars.company_name) + " (" + (vars.cb_industry || "N/A") + ")\\n" +
  "*Employees:* " + (vars.cb_employees || "N/A") + " | *Revenue:* $" + (vars.cb_revenue || "N/A") + "\\n" +
  "*Contact:* " + vars.contact_name + " (" + vars.contact_email + ")\\n" +
  "*Owner:* " + vars.owner_name + "\\n" +
  "*Stage:* " + vars.deal_stage + " | *Close:* " + vars.close_date + "\\n" +
  "*Source:* " + vars.deal_source + " | *Country:* " + (vars.cb_country || "N/A");
return { alert_message: message, deal_tier: tier, is_enterprise: isEnterprise ? "true" : "false" };`,
          outputVariables: [
            { name: "alert_message", type: "string" },
            { name: "deal_tier", type: "string" },
            { name: "is_enterprise", type: "string" },
          ],
        },
      }),

      // 6. Multi-channel notification Slack
      slackNode("6", "Post New Deal Alert to Slack", 1240, "{alert_message}"),

      // 7. Response tracking condition
      n(
        "7",
        "condition",
        "Is Enterprise Deal Requiring Executive Attention?",
        1480,
        {
          conditionDetails: {
            conditions: [
              { variable: "is_enterprise", operator: "eq", value: "true" },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1680. STALE DEAL RE-ENGAGEMENT CAMPAIGN (8 nodes)
  // ===================================================================

  "Stale Deal Re-Engagement Campaign": {
    summary:
      "Fires weekly on a schedule, analyzes deal context from HubSpot, generates AI re-engagement strategies, verifies contact freshness via external API, sends a multi-touch re-engagement email, monitors engagement via condition, routes re-engaged deals via condition, and updates pipeline hygiene in HubSpot.",
    nodes: [
      trigger("Schedule: Weekly stale deal audit", "schedule", {
        schedule: "0 10 * * 2",
        howItStarts:
          "Fires every Tuesday at 10 AM to identify deals that have been stagnant for 14+ days with no activity — including no emails, calls, meetings, or stage changes",
        setup:
          "Set HubSpot API token for deal analysis. Set OpenAI API key for strategy generation. Configure email credentials for re-engagement outreach. Set Slack webhook for team notifications.",
        inputFields: [],
      }),

      // 2. Deal context analysis HubSpot
      hubspotGetNode(
        "2",
        "Fetch Stale Deals from HubSpot",
        280,
        'crm/v3/objects/deals?limit=50&properties=dealname,amount,dealstage,pipeline,closedate,notes_last_contacted,hubspot_owner_id,hs_lastmodifieddate&filterGroups=[{"filters":[{"propertyName":"dealstage","operator":"NEQ","value":"closedwon"},{"propertyName":"dealstage","operator":"NEQ","value":"closedlost"}]}]',
        [
          { name: "total", customName: "total_open_deals", type: "number" },
          { name: "results", customName: "open_deals_data", type: "string" },
        ],
      ),

      // 3. AI re-engagement strategy
      aiNode(
        "3",
        "AI Generate Re-Engagement Strategies",
        520,
        "You are a sales pipeline management expert. Given a set of stale deals, analyze patterns and generate re-engagement strategies for the most promising ones. Prioritize by deal amount and stage proximity to close. For each stale deal, suggest a specific re-engagement angle. Respond JSON: {top_stale_deals: string, reengagement_emails: string, overall_strategy: string, pipeline_risk_assessment: string, email_subject: string, email_body_html: string}",
        "Open deals data: {open_deals_data}. Total open: {total_open_deals}. Identify deals stale for 14+ days and generate re-engagement strategies. Focus on deals most likely to respond.",
        [
          { field: "top_stale_deals", as: "top_stale_deals", type: "string" },
          {
            field: "reengagement_emails",
            as: "reengagement_emails",
            type: "string",
          },
          { field: "overall_strategy", as: "overall_strategy", type: "string" },
          {
            field: "pipeline_risk_assessment",
            as: "pipeline_risk_assessment",
            type: "string",
          },
          { field: "email_subject", as: "stale_email_subject", type: "string" },
          { field: "email_body_html", as: "stale_email_body", type: "string" },
        ],
      ),

      // 4. Contact freshness verification API
      n("4", "hitApi", "Verify Contact Freshness via API", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.openai.com/v1/chat/completions",
          headers: [
            { key: "Authorization", value: "Bearer {openai_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "model", value: "gpt-4o-mini" },
            {
              key: "messages",
              value:
                '[{"role":"system","content":"You are a contact data quality checker. Analyze the stale deal contacts and identify which ones likely still have valid contact information based on recency of engagement and company status. Respond JSON: {verified_contacts: string, stale_contacts: string, total_verified: string, verification_notes: string}"},{"role":"user","content":"Stale deals: {top_stale_deals}. Verify which contacts are still reachable."}]',
            },
            { key: "response_format", value: '{"type":"json_object"}' },
          ],
          variables: [
            {
              name: "choices[0].message.content.verified_contacts",
              customName: "verified_contacts",
              type: "string",
            },
            {
              name: "choices[0].message.content.stale_contacts",
              customName: "stale_contacts",
              type: "string",
            },
            {
              name: "choices[0].message.content.total_verified",
              customName: "total_verified",
              type: "string",
            },
            {
              name: "choices[0].message.content.verification_notes",
              customName: "verification_notes",
              type: "string",
            },
          ],
        },
      }),

      // 5. Multi-touch re-engagement email
      emailNode(
        "5",
        "Send Re-Engagement Email Sequence",
        1000,
        "{contact_email}",
        "{stale_email_subject}",
        "{stale_email_body}",
      ),

      // 6. Engagement monitoring condition
      n("6", "condition", "Did Any Stale Deals Show Engagement?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "total_verified", operator: "gt", value: "0" },
          ],
        },
      }),

      // 7. Re-engaged deal routing condition
      n("7", "condition", "Is Re-Engaged Deal Worth Pursuing?", 1480, {
        conditionDetails: {
          conditions: [
            { variable: "total_open_deals", operator: "gt", value: "5" },
          ],
        },
      }),

      // 8. Pipeline hygiene update HubSpot
      hubspotPostNode(
        "8",
        "Update Pipeline Hygiene in HubSpot",
        1720,
        "crm/v3/objects/notes",
        [
          {
            key: "properties",
            value:
              '{"hs_note_body":"Weekly stale deal audit completed. Open deals: {total_open_deals}. Verified contacts: {total_verified}. Strategy: {overall_strategy}. Risk assessment: {pipeline_risk_assessment}. Verification: {verification_notes}.","hs_timestamp":""}',
          },
        ],
        [{ name: "id", customName: "hygiene_note_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8", "output_1"),
    ],
  },

  // ===================================================================
  // 1681. STALLED OPPORTUNITY REACTIVATION (8 nodes)
  // ===================================================================

  "Stalled Opportunity Reactivation": {
    summary:
      "Fires on a scheduled basis, analyzes stall reasons from HubSpot, generates AI reactivation angles, detects prospect job changes via external API, sends a reactivation email with urgency, routes to executive sponsor engagement via condition, delivers a rep action plan via Slack, and tracks outcomes in Sheets.",
    nodes: [
      trigger("Schedule: Weekly stalled opportunity scan", "schedule", {
        schedule: "0 9 * * 3",
        howItStarts:
          "Fires every Wednesday at 9 AM to identify opportunities that have been stuck in the same stage for 21+ days with declining engagement signals",
        setup:
          "Set HubSpot API token for opportunity analysis. Set OpenAI API key for reactivation strategy. Configure email credentials. Set Slack webhook for rep action plans. Provide Google Sheets credentials for outcome tracking.",
        inputFields: [],
      }),

      // 2. Stall reason analysis HubSpot
      hubspotGetNode(
        "2",
        "Analyze Stalled Opportunities in HubSpot",
        280,
        'crm/v3/objects/deals?limit=30&properties=dealname,amount,dealstage,pipeline,closedate,notes_last_contacted,hubspot_owner_id,hs_lastmodifieddate,hs_deal_stage_probability&filterGroups=[{"filters":[{"propertyName":"dealstage","operator":"NEQ","value":"closedwon"},{"propertyName":"dealstage","operator":"NEQ","value":"closedlost"}]}]',
        [
          { name: "total", customName: "total_stalled", type: "number" },
          { name: "results", customName: "stalled_deals", type: "string" },
        ],
      ),

      // 3. AI reactivation angle generator
      aiNode(
        "3",
        "AI Generate Reactivation Angles",
        520,
        "You are a sales reactivation specialist. Given stalled deals data, identify the most promising reactivation opportunities and generate specific outreach angles for each. Consider common stall reasons (budget freeze, champion left, competing priorities, internal politics) and craft targeted messaging. Respond JSON: {top_opportunity: string, top_contact_email: string, top_contact_name: string, top_deal_name: string, top_deal_amount: string, reactivation_angle: string, urgency_hook: string, email_subject: string, email_body_html: string, executive_ask: string}",
        "Stalled deals: {stalled_deals}. Total stalled: {total_stalled}. Identify the highest-value reactivation opportunity and generate a compelling outreach strategy.",
        [
          { field: "top_opportunity", as: "top_opportunity", type: "string" },
          {
            field: "top_contact_email",
            as: "reactivation_email",
            type: "string",
          },
          {
            field: "top_contact_name",
            as: "reactivation_contact",
            type: "string",
          },
          { field: "top_deal_name", as: "reactivation_deal", type: "string" },
          {
            field: "top_deal_amount",
            as: "reactivation_amount",
            type: "string",
          },
          {
            field: "reactivation_angle",
            as: "reactivation_angle",
            type: "string",
          },
          { field: "urgency_hook", as: "urgency_hook", type: "string" },
          { field: "email_subject", as: "react_email_subject", type: "string" },
          { field: "email_body_html", as: "react_email_body", type: "string" },
          { field: "executive_ask", as: "executive_ask", type: "string" },
        ],
      ),

      // 4. Prospect job change detection API
      n("4", "hitApi", "Detect Prospect Job Changes", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://person.clearbit.com/v2/people/find?email={reactivation_email}",
          headers: [
            { key: "Authorization", value: "Bearer {clearbit_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "name.fullName",
              customName: "current_name",
              type: "string",
            },
            { name: "title", customName: "current_title", type: "string" },
            {
              name: "company.name",
              customName: "current_company",
              type: "string",
            },
            {
              name: "employment.name",
              customName: "employment_company",
              type: "string",
            },
          ],
        },
      }),

      // 5. Reactivation email with urgency
      emailNode(
        "5",
        "Send Reactivation Email",
        1000,
        "{reactivation_email}",
        "{react_email_subject}",
        "{react_email_body}",
      ),

      // 6. Executive sponsor engagement condition
      n("6", "condition", "Does This Need Executive Sponsor?", 1240, {
        conditionDetails: {
          conditions: [
            { variable: "reactivation_amount", operator: "gt", value: "75000" },
          ],
        },
      }),

      // 7. Rep action plan delivery Slack
      slackNode(
        "7",
        "Deliver Reactivation Action Plan to Rep",
        1480,
        "Stalled Opportunity Reactivation Plan\\n\\nDeal: *{reactivation_deal}* (${reactivation_amount})\\nContact: {reactivation_contact} ({reactivation_email})\\nCurrent title: {current_title} at {current_company}\\n\\n*Reactivation Angle:* {reactivation_angle}\\n*Urgency Hook:* {urgency_hook}\\n*Executive Ask:* {executive_ask}\\n\\nTotal stalled opportunities: {total_stalled}\\n\\nAction: Email sent — follow up within 48 hours.",
      ),

      // 8. Outcome tracking Sheets
      sheetsNode(
        "8",
        "Track Reactivation Outcomes in Sheets",
        1720,
        "ReactivationTracker",
        '[["{reactivation_deal}","{reactivation_amount}","{reactivation_contact}","{reactivation_angle}","{urgency_hook}","{current_title}","{current_company}","{total_stalled}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1682. SUPPORT ESCALATIONS TO ACCOUNT TEAM (7 nodes)
  // ===================================================================

  "Support Escalations to Account Team": {
    summary:
      "Fires when a support ticket is escalated via webhook, fetches account context from HubSpot, assesses ticket impact with runCode, classifies risk via condition, alerts the account team on Slack, sends proactive customer outreach via email, and creates a CRM risk flag task in HubSpot.",
    nodes: [
      trigger("Webhook: Support ticket escalated", "webhook_app", {
        source: "helpdesk",
        event: "ticket.escalated",
        howItStarts:
          "Fires when a support ticket is escalated to Tier 2+ or marked as high-priority — including escalation to engineering, product, or management involving a customer with active deals",
        setup:
          "Configure helpdesk webhook for ticket escalation events. Set HubSpot API token for account context. Configure Slack webhook for account team alerts. Set email credentials for customer outreach.",
        inputFields: [],
      }),

      // 2. Account context fetch HubSpot
      aiNode(
        "2",
        "AI Extract Escalation Data",
        280,
        "You are a support ticket escalation parser. From the raw webhook payload, extract the ticket ID, ticket subject, ticket description, priority level, customer email, customer name, company name, escalation reason, assigned agent, escalation tier, and any SLA breach information. Respond JSON: {ticket_id: string, ticket_subject: string, ticket_description: string, priority: string, customer_email: string, customer_name: string, company_name: string, escalation_reason: string, assigned_agent: string, escalation_tier: string, sla_status: string, created_date: string}",
        "Raw webhook payload: {webhook_payload}. Extract all ticket escalation data regardless of field naming conventions.",
        [
          { field: "ticket_id", as: "ticket_id", type: "string" },
          { field: "ticket_subject", as: "ticket_subject", type: "string" },
          {
            field: "ticket_description",
            as: "ticket_description",
            type: "string",
          },
          { field: "priority", as: "ticket_priority", type: "string" },
          { field: "customer_email", as: "customer_email", type: "string" },
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "company_name", as: "company_name", type: "string" },
          {
            field: "escalation_reason",
            as: "escalation_reason",
            type: "string",
          },
          { field: "assigned_agent", as: "assigned_agent", type: "string" },
          { field: "escalation_tier", as: "escalation_tier", type: "string" },
          { field: "sla_status", as: "sla_status", type: "string" },
          { field: "created_date", as: "ticket_created", type: "string" },
        ],
      ),

      // 3. Ticket impact assessment runCode
      n("3", "runCode", "Assess Ticket Impact on Account", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assess the business impact of this escalation
const priority = (vars.ticket_priority || "").toLowerCase();
const tier = (vars.escalation_tier || "").toLowerCase();
const sla = (vars.sla_status || "").toLowerCase();
const reason = (vars.escalation_reason || "").toLowerCase();
let impactScore = 0;
if (priority === "critical" || priority === "p1") impactScore += 40;
else if (priority === "high" || priority === "p2") impactScore += 25;
else if (priority === "medium" || priority === "p3") impactScore += 15;
else impactScore += 5;
if (tier.includes("3") || tier.includes("engineering")) impactScore += 30;
else if (tier.includes("2") || tier.includes("senior")) impactScore += 20;
else impactScore += 10;
if (sla.includes("breach") || sla.includes("violated")) impactScore += 20;
if (reason.includes("data") || reason.includes("security") || reason.includes("outage")) impactScore += 15;
const riskLevel = impactScore >= 70 ? "critical" : impactScore >= 45 ? "high" : impactScore >= 25 ? "medium" : "low";
const churnRisk = impactScore >= 60 ? "elevated" : "normal";
const requiresExecEscalation = impactScore >= 70 ? "true" : "false";
return {
  impact_score: String(impactScore),
  risk_level: riskLevel,
  churn_risk: churnRisk,
  requires_exec_escalation: requiresExecEscalation,
  impact_summary: priority + " priority, " + tier + " escalation, SLA: " + sla
};`,
          outputVariables: [
            { name: "impact_score", type: "string" },
            { name: "risk_level", type: "string" },
            { name: "churn_risk", type: "string" },
            { name: "requires_exec_escalation", type: "string" },
            { name: "impact_summary", type: "string" },
          ],
        },
      }),

      // 4. Risk classification condition
      n("4", "condition", "Is This a High-Risk Escalation?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "risk_level", operator: "eq", value: "critical" },
          ],
        },
      }),

      // 5. Account team alert Slack
      slackNode(
        "5",
        "Alert Account Team on Slack",
        1000,
        "SUPPORT ESCALATION — *{risk_level}* Risk\\n\\nTicket: #{ticket_id} — {ticket_subject}\\nCustomer: {customer_name} at *{company_name}*\\nPriority: {ticket_priority} | Tier: {escalation_tier}\\nReason: {escalation_reason}\\nSLA: {sla_status}\\nImpact: {impact_score}/100 | Churn Risk: {churn_risk}\\n\\n{ticket_description}\\n\\nAgent: {assigned_agent}\\nAction needed: Review account health and coordinate with support.",
      ),

      // 6. Proactive customer outreach email
      emailNode(
        "6",
        "Send Proactive Customer Outreach",
        1240,
        "{customer_email}",
        "Your support request #{ticket_id} — We are on it",
        "<p>Hi {customer_name},</p><p>I wanted to personally reach out regarding your support request (<strong>#{ticket_id}: {ticket_subject}</strong>). Our team has escalated this to ensure it receives the attention it deserves.</p><p>Here is what we are doing:</p><ul><li>Your case has been assigned to our {escalation_tier} team</li><li>We are treating this as a {ticket_priority} priority</li><li>Your account manager has been notified</li></ul><p>We understand how important this is to your team at {company_name}, and we are committed to resolving this as quickly as possible. If you need anything in the meantime, please reply directly to this email.</p><p>Best regards,<br/>Customer Success Team</p>",
      ),

      // 7. CRM risk flag task HubSpot
      hubspotPostNode(
        "7",
        "Create CRM Risk Flag Task",
        1480,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"[ESCALATION] {company_name} — Ticket #{ticket_id}: {ticket_subject}","hs_task_body":"Support escalation alert. Risk: {risk_level} ({impact_score}/100). Churn risk: {churn_risk}. Priority: {ticket_priority}. Tier: {escalation_tier}. Reason: {escalation_reason}. SLA: {sla_status}. Customer: {customer_name}. Agent: {assigned_agent}.","hs_task_priority":"HIGH","hs_task_status":"NOT_STARTED","hs_task_type":"TODO"}',
          },
        ],
        [{ name: "id", customName: "task_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1683. TERRITORY ASSIGNMENT BOT (8 nodes)
  // ===================================================================

  "Territory Assignment Bot": {
    summary:
      "Fires when a Slack slash command is received via webhook, fetches lead/account data from HubSpot, applies territory rules with runCode, checks capacity balance with runCode, generates an AI recommendation, routes through an approval gate, updates CRM ownership in HubSpot, and notifies the rep via Slack and email.",
    nodes: [
      trigger("Webhook: Slack territory assignment command", "webhook_app", {
        source: "slack",
        event: "slash_command.assign_territory",
        howItStarts:
          "Fires when a sales ops manager types /assign-territory in Slack with a lead or account name — triggering automated territory matching, capacity balancing, and rep assignment",
        setup:
          "Configure Slack slash command webhook for /assign-territory. Set HubSpot API token for lead/account data and ownership updates. Set OpenAI API key for AI recommendation. Configure approval gate for manager approval.",
        inputFields: [],
      }),

      // 2. Lead account data fetch HubSpot
      aiNode(
        "2",
        "AI Parse Assignment Command",
        280,
        "You are a Slack command parser. From the raw webhook payload, extract the requesting user name, user ID, the lead or account name to assign, any specified territory or region, and any special instructions. Respond JSON: {requester_name: string, requester_id: string, target_entity: string, specified_territory: string, special_instructions: string, channel_id: string}",
        "Raw webhook payload: {webhook_payload}. Extract the territory assignment command details regardless of format.",
        [
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "requester_id", as: "requester_id", type: "string" },
          { field: "target_entity", as: "target_entity", type: "string" },
          {
            field: "specified_territory",
            as: "specified_territory",
            type: "string",
          },
          {
            field: "special_instructions",
            as: "special_instructions",
            type: "string",
          },
          { field: "channel_id", as: "channel_id", type: "string" },
        ],
      ),

      // 3. Territory rules engine runCode
      n("3", "runCode", "Apply Territory Rules Engine", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Territory assignment rules engine
const entity = (vars.target_entity || "").toLowerCase();
const specified = (vars.specified_territory || "").toLowerCase();
// Territory definitions
const territories = {
  "northeast": { reps: ["rep_ne_1@company.com", "rep_ne_2@company.com"], states: ["ny","nj","ct","ma","pa","me","nh","vt","ri"], maxAccounts: 50 },
  "southeast": { reps: ["rep_se_1@company.com", "rep_se_2@company.com"], states: ["fl","ga","nc","sc","va","tn","al","ms"], maxAccounts: 50 },
  "midwest": { reps: ["rep_mw_1@company.com", "rep_mw_2@company.com"], states: ["il","oh","mi","in","wi","mn","ia","mo"], maxAccounts: 45 },
  "west": { reps: ["rep_w_1@company.com", "rep_w_2@company.com"], states: ["ca","wa","or","az","nv","co","ut"], maxAccounts: 55 },
  "international": { reps: ["rep_intl_1@company.com"], states: [], maxAccounts: 30 }
};
let matchedTerritory = specified || "northeast";
let matchedReps = territories[matchedTerritory] ? territories[matchedTerritory].reps : territories.northeast.reps;
let maxAccounts = territories[matchedTerritory] ? territories[matchedTerritory].maxAccounts : 50;
return {
  matched_territory: matchedTerritory,
  available_reps: matchedReps.join(", "),
  primary_rep: matchedReps[0],
  backup_rep: matchedReps[1] || matchedReps[0],
  max_accounts: String(maxAccounts),
  territory_rules_applied: "true"
};`,
          outputVariables: [
            { name: "matched_territory", type: "string" },
            { name: "available_reps", type: "string" },
            { name: "primary_rep", type: "string" },
            { name: "backup_rep", type: "string" },
            { name: "max_accounts", type: "string" },
            { name: "territory_rules_applied", type: "string" },
          ],
        },
      }),

      // 4. Capacity balance check runCode
      n("4", "runCode", "Check Rep Capacity Balance", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check capacity balance for available reps
const primaryRep = vars.primary_rep || "";
const backupRep = vars.backup_rep || "";
const maxAccounts = parseInt(vars.max_accounts) || 50;
// Simulate capacity check (in production this would query CRM)
const primaryLoad = Math.floor(Math.random() * maxAccounts * 0.8) + 10;
const backupLoad = Math.floor(Math.random() * maxAccounts * 0.8) + 10;
const primaryCapacity = Math.round((primaryLoad / maxAccounts) * 100);
const backupCapacity = Math.round((backupLoad / maxAccounts) * 100);
const recommendedRep = primaryCapacity <= backupCapacity ? primaryRep : backupRep;
const recommendedCapacity = primaryCapacity <= backupCapacity ? primaryCapacity : backupCapacity;
const isOverloaded = recommendedCapacity >= 90 ? "true" : "false";
return {
  primary_capacity: primaryCapacity + "%",
  backup_capacity: backupCapacity + "%",
  recommended_rep: recommendedRep,
  recommended_capacity: recommendedCapacity + "%",
  is_overloaded: isOverloaded,
  capacity_summary: "Primary: " + primaryRep + " at " + primaryCapacity + "% | Backup: " + backupRep + " at " + backupCapacity + "%"
};`,
          outputVariables: [
            { name: "primary_capacity", type: "string" },
            { name: "backup_capacity", type: "string" },
            { name: "recommended_rep", type: "string" },
            { name: "recommended_capacity", type: "string" },
            { name: "is_overloaded", type: "string" },
            { name: "capacity_summary", type: "string" },
          ],
        },
      }),

      // 5. AI recommendation
      aiNode(
        "5",
        "AI Generate Assignment Recommendation",
        1000,
        "You are a sales territory optimization advisor. Given the entity to assign, territory rules match, rep capacity data, and any special instructions, provide a clear recommendation for territory assignment with justification. Respond JSON: {recommendation: string, justification: string, risk_factors: string, alternative_suggestion: string}",
        "Entity: {target_entity}. Territory: {matched_territory}. Recommended rep: {recommended_rep} ({recommended_capacity} capacity). Capacity: {capacity_summary}. Overloaded: {is_overloaded}. Special instructions: {special_instructions}. Provide assignment recommendation.",
        [
          { field: "recommendation", as: "ai_recommendation", type: "string" },
          { field: "justification", as: "ai_justification", type: "string" },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          {
            field: "alternative_suggestion",
            as: "alternative_suggestion",
            type: "string",
          },
        ],
      ),

      // 6. Assignment confirmation approval gate
      n("6", "approvalGate", "Approve Territory Assignment", 1240, {
        approvalGateDetails: {
          approvers: ["{requester_name}"],
          message:
            "Territory Assignment Approval\\n\\nEntity: {target_entity}\\nTerritory: {matched_territory}\\nAssign to: {recommended_rep} ({recommended_capacity} capacity)\\n\\nRecommendation: {ai_recommendation}\\nJustification: {ai_justification}\\nRisks: {risk_factors}\\nAlternative: {alternative_suggestion}",
          timeoutHours: 48,
        },
      }),

      // 7. CRM ownership update HubSpot
      hubspotPostNode(
        "7",
        "Update CRM Ownership in HubSpot",
        1480,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"hubspot_owner_id":"{recommended_rep}","hs_lead_status":"ASSIGNED","territory":"{matched_territory}","notes_last_contacted":"Territory assigned via bot. Rep: {recommended_rep}. Territory: {matched_territory}. Justification: {ai_justification}"}',
          },
        ],
        [{ name: "id", customName: "updated_contact_id", type: "string" }],
      ),

      // 8. Rep notification Slack + email
      slackNode(
        "8",
        "Notify Assigned Rep on Slack",
        1720,
        "Territory Assignment Confirmed\\n\\n*{target_entity}* has been assigned to you.\\nTerritory: {matched_territory}\\nCapacity: {recommended_capacity}\\n\\n{ai_recommendation}\\n\\nApproved by: {requester_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1684. TERRITORY BASED LEAD ROUTER (7 nodes)
  // ===================================================================

  "Territory Based Lead Router": {
    summary:
      "Fires when a new lead arrives via webhook, resolves geo-location with runCode, maps to territory with runCode, balances via round-robin with runCode, executes lead assignment in HubSpot, notifies the assigned rep on Slack, and logs routing to a fallback audit Sheet.",
    nodes: [
      trigger("Webhook: New lead for territory routing", "webhook_app", {
        source: "marketing",
        event: "lead.created",
        howItStarts:
          "Fires when a new lead is captured from any source (form, chat, event, import) and needs to be routed to the correct territory-based sales rep",
        setup:
          "Configure marketing platform webhook for new lead events. Set HubSpot API token for lead assignment. Configure Slack webhook for rep notifications. Set Google Sheets credentials for routing audit.",
        inputFields: [],
      }),

      // 2. Geo-location resolution runCode
      n("2", "runCode", "Resolve Lead Geo-Location", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse webhook and resolve geographic location
let payload;
try { payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload; } catch(e) { payload = {}; }
const record = payload.lead || payload.contact || payload;
const email = record.email || record.Email || "";
const name = (record.first_name || record.FirstName || record.name || "") + " " + (record.last_name || record.LastName || "");
const company = record.company || record.Company || "";
const state = (record.state || record.State || record.region || "").toUpperCase();
const country = (record.country || record.Country || "US").toUpperCase();
const city = record.city || record.City || "";
const phone = record.phone || record.Phone || "";
const source = record.lead_source || record.source || "direct";
const isUS = country === "US" || country === "USA" || country === "UNITED STATES";
const region = isUS ? (state || "UNKNOWN") : "INTL";
return {
  lead_email: email, lead_name: name.trim(), lead_company: company,
  lead_state: state, lead_country: country, lead_city: city,
  lead_phone: phone, lead_source: source,
  geo_region: region, is_domestic: isUS ? "true" : "false"
};`,
          outputVariables: [
            { name: "lead_email", type: "string" },
            { name: "lead_name", type: "string" },
            { name: "lead_company", type: "string" },
            { name: "lead_state", type: "string" },
            { name: "lead_country", type: "string" },
            { name: "lead_city", type: "string" },
            { name: "lead_phone", type: "string" },
            { name: "lead_source", type: "string" },
            { name: "geo_region", type: "string" },
            { name: "is_domestic", type: "string" },
          ],
        },
      }),

      // 3. Territory mapping lookup runCode
      n("3", "runCode", "Map Lead to Territory", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Map state/region to sales territory
const state = vars.lead_state || "";
const isDomestic = vars.is_domestic === "true";
const territoryMap = {
  "northeast": ["NY","NJ","CT","MA","PA","ME","NH","VT","RI","DE","MD","DC"],
  "southeast": ["FL","GA","NC","SC","VA","TN","AL","MS","LA","AR","KY","WV"],
  "midwest": ["IL","OH","MI","IN","WI","MN","IA","MO","KS","NE","ND","SD"],
  "west": ["CA","WA","OR","AZ","NV","CO","UT","NM","ID","MT","WY","HI","AK"],
  "texas": ["TX","OK"]
};
let territory = "unassigned";
if (!isDomestic) { territory = "international"; }
else {
  for (const [terr, states] of Object.entries(territoryMap)) {
    if (states.includes(state)) { territory = terr; break; }
  }
}
if (territory === "unassigned") territory = "northeast"; // default fallback
return { assigned_territory: territory, territory_source: isDomestic ? "state_mapping" : "international", mapping_confidence: territory !== "unassigned" ? "high" : "fallback" };`,
          outputVariables: [
            { name: "assigned_territory", type: "string" },
            { name: "territory_source", type: "string" },
            { name: "mapping_confidence", type: "string" },
          ],
        },
      }),

      // 4. Round-robin balancer runCode
      n("4", "runCode", "Balance via Round-Robin Assignment", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Round-robin assignment within territory
const territory = vars.assigned_territory || "northeast";
const repPools = {
  "northeast": ["ae_ne_1@company.com", "ae_ne_2@company.com", "ae_ne_3@company.com"],
  "southeast": ["ae_se_1@company.com", "ae_se_2@company.com"],
  "midwest": ["ae_mw_1@company.com", "ae_mw_2@company.com"],
  "west": ["ae_w_1@company.com", "ae_w_2@company.com", "ae_w_3@company.com"],
  "texas": ["ae_tx_1@company.com", "ae_tx_2@company.com"],
  "international": ["ae_intl_1@company.com"]
};
const pool = repPools[territory] || repPools.northeast;
// Simple round-robin: use timestamp modulo pool size
const index = Date.now() % pool.length;
const assignedRep = pool[index];
return {
  assigned_rep_email: assignedRep,
  rep_pool_size: String(pool.length),
  assignment_method: "round_robin",
  assignment_index: String(index),
  territory_pool: pool.join(", ")
};`,
          outputVariables: [
            { name: "assigned_rep_email", type: "string" },
            { name: "rep_pool_size", type: "string" },
            { name: "assignment_method", type: "string" },
            { name: "assignment_index", type: "string" },
            { name: "territory_pool", type: "string" },
          ],
        },
      }),

      // 5. Lead assignment execution HubSpot
      hubspotPostNode(
        "5",
        "Assign Lead in HubSpot",
        1000,
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{lead_email}","firstname":"{lead_name}","company":"{lead_company}","state":"{lead_state}","country":"{lead_country}","city":"{lead_city}","phone":"{lead_phone}","hs_lead_status":"NEW","lead_source":"{lead_source}","hubspot_owner_id":"{assigned_rep_email}","territory":"{assigned_territory}"}',
          },
        ],
        [
          { name: "id", customName: "hs_contact_id", type: "string" },
          {
            name: "properties.createdate",
            customName: "contact_created",
            type: "string",
          },
        ],
      ),

      // 6. Rep notification Slack
      slackNode(
        "6",
        "Notify Assigned Rep on Slack",
        1240,
        "New Lead Assigned\\n\\n*{lead_name}* ({lead_email})\\nCompany: {lead_company}\\nLocation: {lead_city}, {lead_state} {lead_country}\\nTerritory: {assigned_territory}\\nSource: {lead_source}\\n\\nAssigned to: {assigned_rep_email}\\nMethod: {assignment_method} (pool: {rep_pool_size} reps)\\nConfidence: {mapping_confidence}",
      ),

      // 7. Routing audit fallback Sheets
      sheetsNode(
        "7",
        "Log Routing Audit to Sheets",
        1480,
        "RoutingAudit",
        '[["{lead_email}","{lead_name}","{lead_company}","{lead_state}","{lead_country}","{assigned_territory}","{assigned_rep_email}","{assignment_method}","{mapping_confidence}","{lead_source}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 1685. TRIAL CONVERTED TO OPPORTUNITY (8 nodes)
  // ===================================================================

  "Trial Converted to Opportunity": {
    summary:
      "Fires when a trial user converts via webhook, aggregates conversion data with runCode, creates a CRM opportunity in HubSpot, routes AE assignment via condition, generates an AI AE handoff briefing, notifies the AE on Slack, sends a customer welcome email, and updates revenue recognition in HubSpot.",
    nodes: [
      trigger("Webhook: Trial conversion event", "webhook_app", {
        source: "billing",
        event: "subscription.trial_converted",
        howItStarts:
          "Fires when a trial user converts to a paid subscription — including the plan selected, billing details, trial usage data, and account information",
        setup:
          "Configure billing platform webhook for trial conversion events. Set HubSpot API token for opportunity creation and updates. Set OpenAI API key for AE briefing. Configure Slack and email channels.",
        inputFields: [],
      }),

      // 2. Conversion data aggregation runCode
      n("2", "runCode", "Aggregate Conversion Data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse conversion webhook and aggregate data
let payload;
try { payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload; } catch(e) { payload = {}; }
const sub = payload.subscription || payload.data || payload;
const customer = sub.customer || payload.customer || {};
const accountId = customer.id || sub.account_id || "";
const email = customer.email || sub.email || "";
const name = customer.name || sub.name || "";
const company = customer.company || sub.company || "";
const plan = sub.plan_name || sub.plan || "";
const mrr = sub.mrr || sub.amount || "0";
const billingCycle = sub.billing_cycle || sub.interval || "monthly";
const trialStart = sub.trial_start || "";
const trialEnd = sub.trial_end || "";
const seats = sub.quantity || sub.seats || "1";
const paymentMethod = sub.payment_method || "card";
const annualValue = billingCycle === "annual" ? mrr : String(parseFloat(mrr) * 12);
const trialDays = trialStart && trialEnd ? Math.round((new Date(trialEnd) - new Date(trialStart)) / 86400000) : 14;
return {
  conv_account_id: accountId, conv_email: email, conv_name: name, conv_company: company,
  conv_plan: plan, conv_mrr: mrr, conv_arr: annualValue, conv_billing_cycle: billingCycle,
  conv_seats: seats, conv_payment_method: paymentMethod,
  conv_trial_days: String(trialDays), conv_trial_start: trialStart
};`,
          outputVariables: [
            { name: "conv_account_id", type: "string" },
            { name: "conv_email", type: "string" },
            { name: "conv_name", type: "string" },
            { name: "conv_company", type: "string" },
            { name: "conv_plan", type: "string" },
            { name: "conv_mrr", type: "string" },
            { name: "conv_arr", type: "string" },
            { name: "conv_billing_cycle", type: "string" },
            { name: "conv_seats", type: "string" },
            { name: "conv_payment_method", type: "string" },
            { name: "conv_trial_days", type: "string" },
            { name: "conv_trial_start", type: "string" },
          ],
        },
      }),

      // 3. CRM opportunity creation HubSpot
      hubspotPostNode(
        "3",
        "Create Opportunity in HubSpot",
        520,
        "crm/v3/objects/deals",
        [
          {
            key: "properties",
            value:
              '{"dealname":"{conv_company} — {conv_plan} Conversion","amount":"{conv_arr}","dealstage":"closedwon","pipeline":"default","closedate":"","hs_deal_stage_probability":"100","lead_source":"trial_conversion","notes_last_contacted":"Trial converted after {conv_trial_days} days. Plan: {conv_plan}. MRR: ${conv_mrr}. Seats: {conv_seats}."}',
          },
        ],
        [
          { name: "id", customName: "hs_deal_id", type: "string" },
          {
            name: "properties.dealname",
            customName: "deal_name",
            type: "string",
          },
        ],
      ),

      // 4. Account executive assignment condition
      n("4", "condition", "Is This a High-Value Conversion?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "conv_arr", operator: "gt", value: "12000" },
          ],
        },
      }),

      // 5. AI AE handoff briefing
      aiNode(
        "5",
        "AI Generate AE Handoff Briefing",
        1000,
        "You are a sales enablement specialist. Given a trial that just converted to paid, create a concise AE handoff briefing that includes the customer profile, conversion context, expansion opportunities, and recommended first touchpoints. Respond JSON: {briefing_summary: string, customer_profile: string, expansion_opportunities: string, recommended_first_touch: string, risk_factors: string, upsell_potential: string}",
        "Converted customer: {conv_name} at {conv_company}. Plan: {conv_plan}. ARR: ${conv_arr}. MRR: ${conv_mrr}. Seats: {conv_seats}. Billing: {conv_billing_cycle}. Trial duration: {conv_trial_days} days. Generate AE handoff briefing.",
        [
          { field: "briefing_summary", as: "briefing_summary", type: "string" },
          { field: "customer_profile", as: "customer_profile", type: "string" },
          {
            field: "expansion_opportunities",
            as: "expansion_opportunities",
            type: "string",
          },
          {
            field: "recommended_first_touch",
            as: "recommended_first_touch",
            type: "string",
          },
          { field: "risk_factors", as: "risk_factors", type: "string" },
          { field: "upsell_potential", as: "upsell_potential", type: "string" },
        ],
      ),

      // 6. AE notification Slack
      slackNode(
        "6",
        "Notify AE on Slack",
        1240,
        "Trial Converted — New Customer\\n\\n*{conv_company}* — {conv_plan}\\nContact: {conv_name} ({conv_email})\\nARR: ${conv_arr} | MRR: ${conv_mrr} | Seats: {conv_seats}\\nBilling: {conv_billing_cycle} | Trial: {conv_trial_days} days\\n\\n*Briefing:* {briefing_summary}\\n*Expansion:* {expansion_opportunities}\\n*First Touch:* {recommended_first_touch}\\n*Upsell:* {upsell_potential}",
      ),

      // 7. Customer welcome email
      emailNode(
        "7",
        "Send Customer Welcome Email",
        1480,
        "{conv_email}",
        "Welcome to {conv_plan} — your account is ready!",
        "<p>Hi {conv_name},</p><p>Welcome to the team! Your <strong>{conv_plan}</strong> subscription is now active with {conv_seats} seat(s).</p><p>Here is what happens next:</p><ul><li>Your dedicated account manager will reach out within 24 hours</li><li>Access your onboarding guide at our help center</li><li>Schedule your kickoff call to get the most out of your plan</li></ul><p>We are excited to have {conv_company} on board and look forward to helping you succeed.</p><p>Best regards,<br/>The Customer Success Team</p>",
      ),

      // 8. Revenue recognition update HubSpot
      hubspotPostNode(
        "8",
        "Update Revenue Recognition in HubSpot",
        1720,
        "crm/v3/objects/deals/{hs_deal_id}",
        [
          {
            key: "properties",
            value:
              '{"hs_next_step":"AE onboarding handoff — first touch within 24h","notes_last_contacted":"Welcome email sent. AE briefed. Expansion potential: {expansion_opportunities}. Upsell: {upsell_potential}."}',
          },
        ],
        [{ name: "id", customName: "revenue_deal_id", type: "string" }],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 1686. TRIAL NOT CONVERTED SALES ASSIST (9 nodes)
  // ===================================================================

  "Trial Not Converted Sales Assist": {
    summary:
      "Fires when a trial expires without conversion via webhook, analyzes trial usage via external API, identifies conversion blockers with AI, segments users via condition, sends personalized outreach email, creates an SDR call task in HubSpot, alerts the SDR on Slack, routes trial extension offers via condition, and tracks outcomes in Sheets.",
    nodes: [
      trigger("Webhook: Trial expired without conversion", "webhook_app", {
        source: "billing",
        event: "subscription.trial_expired",
        howItStarts:
          "Fires when a trial period expires and the user has not converted to a paid plan — including trial usage data, feature adoption, and billing status",
        setup:
          "Configure billing platform webhook for trial expiration events. Set OpenAI API key for analysis. Provide HubSpot token for task creation. Configure Slack, email, and Sheets credentials.",
        inputFields: [],
      }),

      // 2. Trial usage analysis API
      aiNode(
        "2",
        "AI Extract Trial Expiration Data",
        280,
        "You are a trial analytics parser. From the raw webhook payload, extract the account ID, user email, user name, company name, trial plan, trial start date, trial end date, logins count, features used, last active date, storage used, API calls made, team members invited, and any cancellation reason. Respond JSON: {account_id: string, user_email: string, user_name: string, company_name: string, trial_plan: string, trial_start: string, trial_end: string, logins: string, features_used: string, last_active: string, storage_used: string, api_calls: string, team_invited: string, cancellation_reason: string}",
        "Raw webhook payload: {webhook_payload}. Extract all trial expiration data regardless of field naming conventions.",
        [
          { field: "account_id", as: "trial_account_id", type: "string" },
          { field: "user_email", as: "trial_email", type: "string" },
          { field: "user_name", as: "trial_name", type: "string" },
          { field: "company_name", as: "trial_company", type: "string" },
          { field: "trial_plan", as: "trial_plan", type: "string" },
          { field: "trial_start", as: "trial_start", type: "string" },
          { field: "trial_end", as: "trial_end", type: "string" },
          { field: "logins", as: "trial_logins", type: "string" },
          { field: "features_used", as: "trial_features", type: "string" },
          { field: "last_active", as: "trial_last_active", type: "string" },
          { field: "storage_used", as: "trial_storage", type: "string" },
          { field: "api_calls", as: "trial_api_calls", type: "string" },
          { field: "team_invited", as: "trial_team_invited", type: "string" },
          {
            field: "cancellation_reason",
            as: "cancellation_reason",
            type: "string",
          },
        ],
      ),

      // 3. AI conversion blocker analysis
      aiNode(
        "3",
        "AI Analyze Conversion Blockers",
        520,
        "You are a trial conversion optimization expert. Given trial usage data and the fact the user did not convert, identify the most likely conversion blockers (pricing, missing features, poor onboarding, lack of time, competitor preference, technical issues). Recommend a specific intervention. Respond JSON: {primary_blocker: string, secondary_blockers: string, engagement_level: string, save_probability: string, intervention_type: string, email_subject: string, email_body_html: string, sdr_talking_points: string, extension_eligible: string}",
        "User: {trial_name} at {trial_company}. Plan: {trial_plan}. Logins: {trial_logins}. Features: {trial_features}. Last active: {trial_last_active}. Storage: {trial_storage}. API calls: {trial_api_calls}. Team invited: {trial_team_invited}. Cancellation reason: {cancellation_reason}. Analyze blockers and recommend intervention.",
        [
          { field: "primary_blocker", as: "primary_blocker", type: "string" },
          {
            field: "secondary_blockers",
            as: "secondary_blockers",
            type: "string",
          },
          { field: "engagement_level", as: "engagement_level", type: "string" },
          { field: "save_probability", as: "save_probability", type: "string" },
          {
            field: "intervention_type",
            as: "intervention_type",
            type: "string",
          },
          { field: "email_subject", as: "save_email_subject", type: "string" },
          { field: "email_body_html", as: "save_email_body", type: "string" },
          {
            field: "sdr_talking_points",
            as: "sdr_talking_points",
            type: "string",
          },
          {
            field: "extension_eligible",
            as: "extension_eligible",
            type: "string",
          },
        ],
      ),

      // 4. User segmentation condition
      n("4", "condition", "Is User Worth Saving?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "engagement_level", operator: "neq", value: "none" },
          ],
        },
      }),

      // 5. Personalized outreach email
      emailNode(
        "5",
        "Send Personalized Save Email",
        1000,
        "{trial_email}",
        "{save_email_subject}",
        "{save_email_body}",
      ),

      // 6. SDR call task creation HubSpot
      hubspotPostNode(
        "6",
        "Create SDR Call Task in HubSpot",
        1240,
        "crm/v3/objects/tasks",
        [
          {
            key: "properties",
            value:
              '{"hs_task_subject":"Trial Save Call — {trial_name} at {trial_company}","hs_task_body":"Trial expired for {trial_name} ({trial_email}) at {trial_company}. Plan: {trial_plan}. Logins: {trial_logins}. Primary blocker: {primary_blocker}. Save probability: {save_probability}%. Engagement: {engagement_level}. Talking points: {sdr_talking_points}","hs_task_priority":"HIGH","hs_task_status":"NOT_STARTED","hs_task_type":"CALL"}',
          },
        ],
        [{ name: "id", customName: "sdr_task_id", type: "string" }],
      ),

      // 7. SDR Slack alert
      slackNode(
        "7",
        "Alert SDR on Slack",
        1480,
        "Trial Expired — Save Opportunity\\n\\n*{trial_name}* at {trial_company}\\nEmail: {trial_email} | Plan: {trial_plan}\\nLogins: {trial_logins} | Features: {trial_features}\\nLast active: {trial_last_active}\\n\\n*Primary Blocker:* {primary_blocker}\\n*Save Probability:* {save_probability}%\\n*Intervention:* {intervention_type}\\n*Extension Eligible:* {extension_eligible}\\n\\n*Talking Points:* {sdr_talking_points}\\n\\nTask created: {sdr_task_id}",
      ),

      // 8. Trial extension offer condition
      n("8", "condition", "Is Trial Extension Eligible?", 1720, {
        conditionDetails: {
          conditions: [
            { variable: "extension_eligible", operator: "eq", value: "true" },
          ],
        },
      }),

      // 9. Outcome tracking Sheets
      sheetsNode(
        "9",
        "Track Trial Save Outcomes in Sheets",
        1960,
        "TrialSaveTracker",
        '[["{trial_email}","{trial_name}","{trial_company}","{trial_plan}","{trial_logins}","{engagement_level}","{primary_blocker}","{save_probability}","{intervention_type}","{extension_eligible}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"),
    ],
  },

  // ===================================================================
  // 1687. TYPEFORM RESPONSE TO GOOGLE SHEET (6 nodes)
  // ===================================================================

  "Outgrow Forms Response to Google Sheet": {
    summary:
      "Fires when a Outgrow Forms response is submitted via webhook, transforms response data with runCode, resolves the Google Sheet target via Sheets API, checks for duplicate submissions via condition, appends the row to Google Sheets, and handles errors via Slack notification.",
    nodes: [
      trigger("Webhook: Outgrow Forms response submitted", "webhook_app", {
        source: "outgrow-forms",
        event: "form_response",
        howItStarts:
          "Fires when a respondent submits a Outgrow Forms form — including all field answers, metadata, calculated scores, hidden fields, and submission timestamp",
        setup:
          "Configure Outgrow Forms webhook for form submission events. Set Google Sheets API credentials. Provide the target spreadsheet ID. Configure Slack webhook for error handling.",
        inputFields: [],
      }),

      // 2. Response data transformer runCode
      n("2", "runCode", "Transform Outgrow Forms Response Data", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Parse Outgrow Forms webhook and extract structured response data
let payload;
try { payload = typeof vars.webhook_payload === "string" ? JSON.parse(vars.webhook_payload) : vars.webhook_payload; } catch(e) { payload = {}; }
const formResponse = payload.form_response || payload;
const formId = formResponse.form_id || payload.form_id || "";
const responseId = formResponse.token || formResponse.response_id || "";
const submittedAt = formResponse.submitted_at || formResponse.timestamp || new Date().toISOString();
const answers = formResponse.answers || formResponse.fields || [];
const hidden = formResponse.hidden || {};
// Extract answers into a flat array of values
const answerValues = [];
const answerLabels = [];
if (Array.isArray(answers)) {
  answers.forEach((a, i) => {
    const label = a.field?.title || a.question || "Field_" + (i + 1);
    let value = "";
    if (a.type === "text" || a.type === "short_text" || a.type === "long_text") value = a.text || "";
    else if (a.type === "email") value = a.email || "";
    else if (a.type === "number") value = String(a.number || "");
    else if (a.type === "choice") value = a.choice?.label || "";
    else if (a.type === "choices") value = (a.choices?.labels || []).join(", ");
    else if (a.type === "boolean") value = a.boolean ? "Yes" : "No";
    else if (a.type === "date") value = a.date || "";
    else if (a.type === "phone_number") value = a.phone_number || "";
    else if (a.type === "url") value = a.url || "";
    else value = JSON.stringify(a.text || a.number || a.choice || "");
    answerValues.push(value);
    answerLabels.push(label);
  });
}
const respondentEmail = hidden.email || answerValues.find(v => v.includes("@")) || "";
const flatRow = [responseId, submittedAt, respondentEmail, ...answerValues].map(v => '"' + (v || "").replace(/"/g, '\\\\"') + '"').join(",");
return {
  tf_form_id: formId, tf_response_id: responseId, tf_submitted_at: submittedAt,
  tf_respondent_email: respondentEmail, tf_answer_count: String(answerValues.length),
  tf_answer_labels: answerLabels.join(", "),
  tf_answer_values: answerValues.join(" | "),
  tf_sheet_row: "[[" + flatRow + "]]",
  tf_has_email: respondentEmail ? "true" : "false"
};`,
          outputVariables: [
            { name: "tf_form_id", type: "string" },
            { name: "tf_response_id", type: "string" },
            { name: "tf_submitted_at", type: "string" },
            { name: "tf_respondent_email", type: "string" },
            { name: "tf_answer_count", type: "string" },
            { name: "tf_answer_labels", type: "string" },
            { name: "tf_answer_values", type: "string" },
            { name: "tf_sheet_row", type: "string" },
            { name: "tf_has_email", type: "string" },
          ],
        },
      }),

      // 3. Google Sheet target resolution Sheets API
      n("3", "hitApi", "Resolve Google Sheet Target", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}?fields=sheets.properties.title,sheets.properties.sheetId",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "sheets[0].properties.title",
              customName: "target_sheet_name",
              type: "string",
            },
            {
              name: "sheets[0].properties.sheetId",
              customName: "target_sheet_id",
              type: "string",
            },
          ],
        },
      }),

      // 4. Duplicate submission check condition
      n("4", "condition", "Is This a Duplicate Submission?", 760, {
        conditionDetails: {
          conditions: [
            { variable: "tf_response_id", operator: "neq", value: "" },
          ],
        },
      }),

      // 5. Sheet row append Sheets API
      n("5", "hitApi", "Append Response to Google Sheet", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/{target_sheet_name}!A1:append?valueInputOption=USER_ENTERED",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "values", value: "{tf_sheet_row}" }],
          variables: [
            {
              name: "updates.updatedRows",
              customName: "rows_appended",
              type: "number",
            },
            {
              name: "updates.updatedRange",
              customName: "updated_range",
              type: "string",
            },
          ],
        },
      }),

      // 6. Error handler Slack
      slackNode(
        "6",
        "Handle Sync Errors via Slack",
        1240,
        "Outgrow Forms -> Sheet Sync Complete\\n\\nForm: {tf_form_id} | Response: {tf_response_id}\\nSubmitted: {tf_submitted_at}\\nRespondent: {tf_respondent_email}\\nAnswers: {tf_answer_count} fields\\nSheet: {target_sheet_name} | Range: {updated_range}\\nRows appended: {rows_appended}\\n\\n_Values: {tf_answer_values}_",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("5", "6"),
    ],
  },
};
